import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Tooltip, message, Spin, Modal, Input, InputNumber } from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    MailOutlined,
    PhoneOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    ArrowRightOutlined,
    LoadingOutlined,
    CloseOutlined,
    HistoryOutlined,
    AppstoreOutlined,
    FilterOutlined,
    SlidersOutlined,
    PlusOutlined,
    PictureOutlined,
    FormOutlined,
    DownloadOutlined,
    ThunderboltOutlined,
    SafetyCertificateOutlined,
    IdcardOutlined,
    ReadOutlined,
    BlockOutlined,
    TrophyOutlined,
    StarOutlined,
    DollarCircleOutlined,
} from "@ant-design/icons";
import Sidemenu from "../../components/sidemenu";
// Same full-screen blurred loader CallerForm uses for "Fetch Resume Details",
// kept as a local copy (./Loader.jsx) so it needs no cross-folder import.
import Loader from "./Loader";
import { functionService } from "../../lib/functions";
import * as S from "./Styled";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Keyword search is meant for ~5-6 short tokens, not a full JD.
const KEYWORD_QUERY_MAX_LENGTH = 200;

// JD images (+ → Add Image). Same 2MB limit as JD PDFs. The backend
// checks the real file type again before OCR.
const JD_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const JD_IMAGE_EXT = /\.(png|jpe?g|webp)$/i;
const JD_MAX_BYTES = 2 * 1024 * 1024;
const isJdImageFile = (file) => !!file && JD_IMAGE_TYPES.includes(file.type) && JD_IMAGE_EXT.test(file.name || "");


/* ── Right-hand panel: recent searches + open demands ── */

// Recent keyword searches live only in this browser (localStorage), per
// device. Nothing is sent to the server. Every read/write is guarded, so a
// blocked or full storage just means "no history", never a crash.
// Each entry also keeps the filters its query was parsed into, so clicking
// it (or typing the same words again) re-runs the search with NO parse/LLM
// call. Bump RECENT_FILTERS_VERSION if the keyword parser's output changes,
// so older saved filters are re-parsed instead of reused.
const RECENT_SEARCHES_KEY = "coffeeePlus.recentSearches";
const MAX_RECENT_SEARCHES = 8;
const RECENT_FILTERS_VERSION = 1;

// Same normalisation as the backend parse cache: case and extra spaces
// don't make a query "different".
const normalizeQueryKey = (query) => String(query || "").toLowerCase().replace(/\s+/g, " ").trim();

const loadRecentSearches = () => {
    try {
        const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list)
            ? list.filter((item) => item && typeof item.query === "string" && item.query.trim()).slice(0, MAX_RECENT_SEARCHES)
            : [];
    } catch (err) {
        return [];
    }
};

const saveRecentSearches = (list) => {
    try {
        window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list));
    } catch (err) {
        // storage unavailable — history simply isn't kept
    }
};

// Newest first, case/space-insensitive de-dupe, capped. `filters` are the
// parsed keyword filters for this query (stored so it can re-run without a
// parse call).
const addToRecentSearches = (list, query, filters) => {
    const key = normalizeQueryKey(query);
    const rest = list.filter((item) => normalizeQueryKey(item.query) !== key);
    const entry = { query: query.trim(), at: Date.now() };
    if (filters && typeof filters === "object") {
        entry.filters = filters;
        entry.v = RECENT_FILTERS_VERSION;
    }
    return [entry, ...rest].slice(0, MAX_RECENT_SEARCHES);
};

// Saved filters for a query, or null if there are none (or they were saved
// by an older parser version).
const findSavedFilters = (list, query) => {
    const key = normalizeQueryKey(query);
    const hit = (Array.isArray(list) ? list : []).find(
        (item) => normalizeQueryKey(item.query) === key
    );
    return hit && hit.v === RECENT_FILTERS_VERSION && hit.filters && typeof hit.filters === "object"
        ? hit.filters
        : null;
};

const formatTimeAgo = (timestamp) => {
    const diff = Math.max(0, Date.now() - Number(timestamp || 0));
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return days === 1 ? "yesterday" : `${days} days ago`;
};

// How many open demands the panel shows. High enough that a typical
// recruiter's live list fills the side panel instead of leaving a
// blank stretch at the bottom.
const MAX_OPEN_DEMANDS = 30;

const DEMAND_EXP_BUCKETS = [
    { key: "0-4", label: "0–4 yrs", min: 0, max: 4 },
    { key: "4-8", label: "4–8 yrs", min: 4, max: 8 },
    { key: "8+", label: "8+ yrs", min: 8, max: null },
];

// 2.0 -> "2", 2.50 -> "2.5"; null for missing/non-numeric values.
const formatShortNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return null;
    return String(Math.round(n * 10) / 10);
};

// "3–5 yrs · 12–18 LPA" style summary line for a demand row; skips empty parts.
const describeDemand = (d) => {
    const minY = formatShortNumber(d.min_yrs);
    const maxY = formatShortNumber(d.max_yrs);
    const minC = formatShortNumber(d.min_ctc);
    const maxC = formatShortNumber(d.max_ctc);
    const parts = [];
    if (minY !== null && maxY !== null) parts.push(`${minY}–${maxY} yrs`);
    else if (minY !== null) parts.push(`${minY}+ yrs`);
    else if (maxY !== null) parts.push(`Up to ${maxY} yrs`);
    if (minC !== null && maxC !== null) parts.push(`${minC}–${maxC} LPA`);
    else if (maxC !== null) parts.push(`Up to ${maxC} LPA`);
    else if (minC !== null) parts.push(`${minC}+ LPA`);
    return parts.join(" · ");
};

// Same company + same role = one entry in the panel (the newest one).
const demandGroupKey = (d) =>
    `${String(d?.company_name || "").trim().toLowerCase()}|${String(d?.role_title || "").trim().toLowerCase()}`;


// First city of the demand's location (array, Postgres "{a,b}" text, or plain text).
const firstDemandCity = (location) => {
    let list = [];
    if (Array.isArray(location)) list = location;
    else if (typeof location === "string") list = location.replace(/[{}"]/g, "").split(",");
    const city = list.map((c) => String(c || "").trim()).find((c) => c && !/^remote$/i.test(c));
    return city || null;
};





/* ─────────────────────────────────────────────
   Helpers — pure display formatting only, nothing here
   assumes a specific API response shape beyond the
   candidate_details field names given in the brief.
───────────────────────────────────────────── */
const getCandidateName = (candidate) => {
    const first = (candidate?.first_name || "").trim();
    const last = (candidate?.last_name || "").trim();
    if (!first && !last) return "-";
    return last ? `${first} ${last}` : first;
};

const getInitials = (name) => {
    if (!name || name === "-") return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
};

// Profile photo (from the candidate's platform profile, aboutme.file_image)
// with the initials as fallback: no URL, a non-http(s) URL, or an image that
// fails to load (expired/blocked link) all show the initials instead.
const isSafePhotoUrl = (url) => typeof url === "string" && /^https?:\/\//i.test(url.trim());

const CandidateAvatar = ({ name, photoUrl, size }) => {
    const [failed, setFailed] = useState(false);

    // A different candidate/URL gets a fresh attempt.
    useEffect(() => {
        setFailed(false);
    }, [photoUrl]);

    const showPhoto = isSafePhotoUrl(photoUrl) && !failed;

    return (
        <S.Avatar
            $size={size}
            $bg={S.getAvatarColor(name)}
            style={showPhoto ? { overflow: "hidden", background: "#EEF0F7" } : undefined}
        >
            {showPhoto ? (
                <img
                    src={photoUrl.trim()}
                    alt={name && name !== "-" ? name : "Candidate"}
                    loading="lazy"
                    // Google profile photos (lh3.googleusercontent.com) can refuse
                    // requests that carry a referrer from another site.
                    referrerPolicy="no-referrer"
                    onError={() => setFailed(true)}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
            ) : (
                getInitials(name)
            )}
        </S.Avatar>
    );
};

const formatExperience = (value) =>
    value === null || value === undefined || value === "" ? "-" : `${value} yrs`;

const formatCtc = (value) =>
    value === null || value === undefined || value === "" ? "-" : `${value} LPA`;

// Chip labels for the parsed keyword filters shown under the search box.
const formatExperienceRange = (min, max) => {
    if (min !== null && max !== null) return min === max ? `${min} yrs` : `${min}–${max} yrs`;
    if (min !== null) return `${min}+ yrs`;
    if (max !== null) return `Up to ${max} yrs`;
    return null;
};

const formatBudgetRange = (min, max, minStrict, maxStrict) => {
    if (min !== null && max !== null) return `Budget ${min}–${max} LPA`;
    if (max !== null) return maxStrict ? `Budget under ${max} LPA` : `Budget up to ${max} LPA`;
    if (min !== null) return minStrict ? `Budget above ${min} LPA` : `Budget ${min}+ LPA`;
    return null;
};

const formatCtcRange = (min, max) => {
    if (min !== null && max !== null) return `CTC ${min}–${max} LPA`;
    if (max !== null) return `CTC up to ${max} LPA`;
    if (min !== null) return `CTC ${min}+ LPA`;
    return null;
};

const demandMatchesExpBucket = (demand, bucket) => {
    if (!bucket) return true;
    const minY = Number(demand?.min_yrs);
    const maxY = Number(demand?.max_yrs);
    const hasMin = Number.isFinite(minY);
    const hasMax = Number.isFinite(maxY);
    if (!hasMin && !hasMax) return false;
    const lo = hasMin ? minY : maxY;
    const hi = hasMax ? maxY : minY;
    if (bucket.max === null) return hi >= bucket.min;
    return lo <= bucket.max && hi >= bucket.min;
};

// Hover text for the match score, e.g.
// "Match score 87 — Skills/Role 40/50 · CTC 20/20 · Experience 15/20 · Location 10/10".
// The breakdown values are points; they add up to match_score.
const SCORE_LABELS = {
    skills_or_role: "Skills/Role",
    ctc: "CTC",
    experience: "Experience",
    location: "Location",
};
const SCORE_MAX_POINTS = {
    skills_or_role: 50,
    ctc: 20,
    experience: 20,
    location: 10,
};
const describeScoreBreakdown = (score, breakdown) => {
    if (!breakdown || typeof breakdown !== "object") return "Match score";
    const parts = Object.entries(breakdown)
        .filter(([key, value]) => SCORE_LABELS[key] && Number.isFinite(Number(value)))
        .map(
            ([key, value]) =>
                `${SCORE_LABELS[key]} ${Math.round(Number(value) * 10) / 10}/${SCORE_MAX_POINTS[key]}`
        );
    const total = Number.isFinite(Number(score)) ? Math.round(Number(score) * 10) / 10 : null;
    return parts.length
        ? `Match score${total !== null ? ` ${total}` : ""} — ${parts.join(" · ")}`
        : "Match score";
};

// Display formatting only — the backend is the sole source of
// match_score, this never computes or adjusts a score, only rounds
// the given number to one decimal place for display (same spirit as
// formatCtc/formatExperience above). Returns null (not shown) for
// null/undefined/non-numeric input.
const formatMatchPercentage = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    return `${Math.round(num * 10) / 10}%`;
};

// Display formatting only — reads the existing created_at field as-is and
// renders it in a clean "24 Sep 2026, 10:35 AM" format (date + time).
// Returns null (not shown) for missing/invalid input.
const formatCreatedDate = (value) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const datePart = date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

    const now = new Date();
    const diffMs = Math.max(0, now.getTime() - date.getTime());

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let ageText;

    if (diffMinutes < 60) {
        ageText = diffMinutes <= 1 ? "just now" : `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
        ageText = diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
    } else if (diffDays < 7) {
        ageText = diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
    } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        ageText = weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
    } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        ageText = months === 1 ? "1 month ago" : `${months} months ago`;
    } else {
        const years = Math.floor(diffDays / 365);
        ageText = years === 1 ? "1 year ago" : `${years} years ago`;
    }

    return `${datePart} (${ageText})`;
};

// "Role at Company" — used as the hover text of the Current Role item.
const getCurrentPosition = (candidate) => {
    const role = (candidate?.current_role || "").trim();
    const company = (candidate?.current_company || "").trim();
    if (role && company) return `${role} at ${company}`;
    return role || company || "";
};

// The search endpoint returns a single flat `skills` array of names. Since the
// backend now combines the searched skills that matched (profile OR resume)
// with the candidate's profile skills — searched ones first — this simply
// shows the first four and tucks the rest behind "+N". (The candidate DETAIL
// endpoint is a different, richer shape — skills: { primary, other } — kept
// separate on purpose.)
const getSkillsSummary = (candidate) => {
    const all = Array.isArray(candidate?.skills) ? candidate.skills.filter(Boolean) : [];

    if (all.length === 0) {
        return { visible: [], rest: [] };
    }

    return {
        visible: all.slice(0, 4),
        rest: all.slice(4),
    };
};

// `location` is a list of cities. Older parser responses and recent searches
// saved before multi-city support hold a single string, so both shapes are
// accepted: "Noida" -> ["Noida"]; null/""/missing -> []. Case-insensitive
// duplicates are dropped (first spelling kept).
const normalizeSearchLocations = (value) => {
    if (Array.isArray(value)) {
        return value
            .filter(
                (location) =>
                    typeof location === "string" &&
                    location.trim()
            )
            .map((location) => location.trim());
    }

    if (typeof value === "string" && value.trim()) {
        return [value.trim()];
    }

    return [];
};

// Case-insensitive de-dupe. Trims whitespace, keys on the lowercased
// value, and keeps the first-seen casing. Used by buildJdFilters to clean
// the parsed JD skills list.
const dedupeCaseInsensitive = (values) => {
    const seen = new Set();
    const result = [];
    values.forEach((raw) => {
        if (raw === null || raw === undefined) return;
        const trimmed = String(raw).trim();
        if (!trimmed) return;
        const key = trimmed.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        result.push(trimmed);
    });
    return result;
};

// Defensive normalization for JD-parser output before it reaches the
// numeric filter states — never lets malformed AI output crash the page.
const normalizeNumberOrNull = (val) => {
    if (val === null || val === undefined || val === "") return null;
    const num = Number(val);
    return Number.isFinite(num) ? num : null;
};

const buildPageNumbers = (currentPage, totalPages) => {
    const pages = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
        return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
};

/* ─────────────────────────────────────────────
   Refine — extra filters over the ORIGINAL result set.
   Only these five filters exist. They never change the original search
   (role / skills / criteria), the scores or the ranking: the original
   criteria are sent unchanged together with a separate `refine` block, and the
   backend filters the already-eligible rows (so totals and paging are right).
───────────────────────────────────────────── */
const ASSESSMENT_REFINE_OPTIONS = [
    { value: "not_started", label: "Not Started" },
    { value: "completed", label: "Completed" },
];

const INTERVIEW_REFINE_OPTIONS = [
    { value: "interview_given", label: "Interview Given" },
    { value: "interview_not_given", label: "Interview Not Given" },
];

const emptyRefine = () => ({
    locations: [],
    experience_min: null,
    experience_max: null,
    budget_min: null,
    budget_max: null,
    assessment: [],
    interview: [],
});

const isRefineActive = (r) =>
    Boolean(
        r &&
            ((r.locations && r.locations.length > 0) ||
                r.experience_min !== null ||
                r.experience_max !== null ||
                r.budget_min !== null ||
                r.budget_max !== null ||
                (r.assessment && r.assessment.length > 0) ||
                (r.interview && r.interview.length > 0))
    );

// Body of the `refine` field of the search request (null = no refinement).
const refineToPayload = (r) =>
    isRefineActive(r)
        ? {
              location: normalizeSearchLocations(r.locations),
              experience_min: r.experience_min ?? null,
              experience_max: r.experience_max ?? null,
              budget_min: r.budget_min ?? null,
              budget_max: r.budget_max ?? null,
              assessment_status: r.assessment || [],
              interview_status: r.interview || [],
          }
        : null;

const optionLabel = (options, value) => options.find((o) => o.value === value)?.label || value;

// Score panel on the card. Display only — every value comes from the backend
// (or, for Fitment Analysis, from the AI score generated from the card or the
// popup); a value that is not available is shown as "—".
const toPercentLabel = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? `${Math.round(n)}%` : null;
};

// Tech scores below this are hidden everywhere (card + "View more" popup).
const MIN_VISIBLE_TECH_SCORE = 65;
const isTechScoreVisible = (value) => {
    if (value === null || value === undefined || value === "") return false;
    const n = Number(value);
    return Number.isFinite(n) && n >= MIN_VISIBLE_TECH_SCORE;
};

// Rounded technical score for the card's "Test Score" cell, or null.
const getTechScoreValue = (candidate) =>
    isTechScoreVisible(candidate?.tech_score) ? Math.round(Number(candidate.tech_score)) : null;

// What produced the current results, for "Generate AI score":
//   { type: "jd_text", value: <pasted JD text>, label }
//   { type: "jd_url",  value: <JD PDF link>,    label }  (uploaded PDF or open demand)
//   { type: "jd_image", value: <JD image link>, label }  (uploaded PNG/JPG/WEBP)
//   { type: "keyword", value: <parsed filters>,  label }
const searchSourceToAiPayload = (source) => {
    if (!source) return null;
    if (source.type === "jd_text") return { jd_text: source.value };
    if (source.type === "jd_url") return { jd_url: source.value };
    if (source.type === "jd_image") return { jd_image_url: source.value };
    const f = source.value || {};
    return {
        keyword_criteria: {
            role_name: f.role_name ?? null,
            skills: Array.isArray(f.skills) ? f.skills : [],
            location: normalizeSearchLocations(f.location),
            experience_min: f.experience_min ?? null,
            experience_max: f.experience_max ?? null,
            budget_min: f.budget_min ?? null,
            budget_max: f.budget_max ?? null,
            budget_min_strict: f.budget_min_strict === true,
            budget_max_strict: f.budget_max_strict === true,
        },
    };
};

const aiScoreKey = (candidateId, source) =>
    candidateId && source
        ? `${candidateId}|${source.type}|${typeof source.value === "string" ? source.value : JSON.stringify(source.value)}`
        : null;

const safeFileName = (name) =>
    String(name || "candidate").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "candidate";

// Latest interview round the candidate PASSED, or null.
// The backend computes this in SQL (latest_round_cleared) and uses the same
// value for filtering and sorting, so it wins whenever the field is present.
// The fallback below (e.g. the popup's detail payload) implements EXACTLY the
// same rules from candidate_current_status + status_history:
//   * the LATEST status by its changed_at timestamp controls the result; the
//     highest round found in older history never does
//   * (R1) -> not cleared, (R2) -> R1, (R3) -> R2, (R4) -> R3
//   * final-stage statuses (Final Select/Reject, offer statuses) -> R4
//   * Yet to be shortlisted / Discussion Round / Pre-Interview Back Out -> not cleared
//   * any other status (Position on Hold/Closed, Change in Requirement,
//     Duplicate Profile, Profile Reshared, ...) makes no progression: the last
//     status that has a round meaning keeps controlling the result
//   * entries without a valid changed_at count as older than dated ones and keep
//     array order (last = newest); equal changed_at -> the more advanced status
//     wins, then the later array position
//   * candidate_current_status is the newest event only when no history entry
//     carries that status
const ROUND_TAG = /\(R([1-4])\)/i;
const FINAL_STAGE_STATUSES = new Set([
    "final select",
    "final reject",
    "pre offer back out",
    "offer yet to be released",
    "offer released",
    "post offer back out",
    "offer accepted - yet to join",
    "offer accepted - joined",
    "offer accepted - not joined",
    "offer rejected - not joined",
]);
const NOT_CLEARED_STATUSES = new Set(["yet to be shortlisted", "discussion round", "pre-interview back out"]);
const CHANGED_AT_ISO =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?)?(Z|[+-](?:0\d|1[0-4])(?::?[0-5]\d)?)?$/;

const normalizeStatusText = (s) => String(s).trim().toLowerCase().replace(/\s+/g, " ");

// 4 = final stage, 1..3 = round cleared, 0 = not cleared, null = no round meaning.
const statusRoundValue = (normalized) => {
    if (FINAL_STAGE_STATUSES.has(normalized)) return 4;
    const m = normalized.match(ROUND_TAG);
    if (m) return Number(m[1]) - 1;
    if (NOT_CLEARED_STATUSES.has(normalized)) return 0;
    return null;
};

// Milliseconds, or null when missing/invalid. ISO-8601 strings (no offset =
// UTC) and epoch seconds/milliseconds are understood; anything else is invalid.
const parseChangedAt = (value) => {
    if (typeof value === "number") {
        if (!Number.isFinite(value) || value < 0 || value > 4102444800000) return null;
        return value >= 100000000000 ? value : value * 1000;
    }
    if (typeof value !== "string") return null;
    const m = value.match(CHANGED_AT_ISO);
    if (!m) return null;
    const [year, month, day, hour, minute, second] = [m[1], m[2], m[3], m[4], m[5], m[6]].map((x) =>
        x === undefined ? 0 : Number(x)
    );
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const daysInMonth = month === 2 ? (leap ? 29 : 28) : [4, 6, 9, 11].includes(month) ? 30 : 31;
    if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth || hour > 23 || minute > 59 || second > 59) {
        return null;
    }
    let iso = value.replace(" ", "T");
    if (!/T/.test(iso)) iso += "T00:00:00";
    iso = iso.replace(/([+-]\d{2})(\d{2})$/, "$1:$2").replace(/([+-]\d{2})$/, "$1:00");
    if (!/(Z|[+-]\d{2}:\d{2})$/.test(iso)) iso += "Z";
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : null;
};

const parseStatusHistory = (raw) => {
    let history = raw;
    if (typeof history === "string") {
        try {
            history = JSON.parse(history);
        } catch (e) {
            history = [];
        }
    }
    return Array.isArray(history) ? history : [];
};

const getLatestRoundCleared = (candidate) => {
    // Backend value (same logic, already used for filtering and sorting) wins when present.
    if (candidate && Object.prototype.hasOwnProperty.call(candidate, "latest_round_cleared")) {
        const fromServer = Number(candidate.latest_round_cleared);
        return Number.isFinite(fromServer) && fromServer >= 1 ? fromServer : null;
    }

    const events = [];
    parseStatusHistory(candidate?.status_history).forEach((entry, index) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) return;
        if (typeof entry.status !== "string" || !entry.status.trim()) return;
        events.push({
            n: normalizeStatusText(entry.status),
            ts: parseChangedAt(entry.changed_at),
            ord: index + 1,
            pseudo: 0,
        });
    });

    const current = candidate?.candidate_current_status;
    if (typeof current === "string" && current.trim()) {
        const n = normalizeStatusText(current);
        if (!events.some((e) => e.n === n)) {
            events.push({ n, ts: null, ord: Number.MAX_SAFE_INTEGER, pseudo: 1 });
        }
    }

    const ranked = events
        .map((e) => ({ ...e, val: statusRoundValue(e.n) }))
        .filter((e) => e.val !== null)
        .sort((a, b) => {
            if (a.pseudo !== b.pseudo) return b.pseudo - a.pseudo;
            if ((a.ts === null) !== (b.ts === null)) return a.ts === null ? 1 : -1;
            if (a.ts !== b.ts) return b.ts - a.ts;
            const av = a.ts === null ? 0 : a.val;
            const bv = b.ts === null ? 0 : b.val;
            if (av !== bv) return bv - av;
            return b.ord - a.ord;
        });

    const cleared = ranked.length > 0 ? ranked[0].val : 0;
    return cleared >= 1 ? cleared : null;
};

const CoffeeePlusSearch = () => {
    /* ── Keyword search input ──
       Raw text only. Parsing into structured filters happens on the
       backend (Layer 2) and is stored separately in parsedKeywordFilters. */
    const [keywordQuery, setKeywordQuery] = useState("");
    const keywordInputRef = useRef(null);

    /* ── Right-hand panel ── */
    const [recentSearches, setRecentSearches] = useState(loadRecentSearches);
    const [openDemands, setOpenDemands] = useState([]);
    const [demandsLoading, setDemandsLoading] = useState(true);
    const [demandsError, setDemandsError] = useState(false);
    // "Company — Role" when the current JD search came from an open demand.
    const [activeDemandLabel, setActiveDemandLabel] = useState(null);
    const [demandQuery, setDemandQuery] = useState("");
    const [demandLocation, setDemandLocation] = useState(null);
    const [demandExp, setDemandExp] = useState(null);

    // Open demands: loaded once from the existing demand-details-list
    // endpoint. That endpoint splits demands into "today" and "all"
    // (= everything before today), so both are fetched and merged. Rows come
    // one per assigned recruiter, so they are de-duplicated by demand id.
    useEffect(() => {
        let cancelled = false;

        const extractRows = (result) => {
            if (result.status !== "fulfilled" || !result.value?.status) return null;
            const payload = result.value.response?.data?.data;
            if (Array.isArray(payload)) return payload;
            if (Array.isArray(payload?.data)) return payload.data;
            return [];
        };

        Promise.allSettled([
            functionService.getDemandDetails({ section: "today", active: "true", sort: "updated_at", order: "desc" }),
            functionService.getDemandDetails({
                section: "all",
                active: "true",
                sort: "updated_at",
                order: "desc",
                page: 1,
                limit: MAX_OPEN_DEMANDS * 5,
            }),
        ]).then(([today, earlier]) => {
            if (cancelled) return;

            const todayRows = extractRows(today);
            const earlierRows = extractRows(earlier);

            if (todayRows === null && earlierRows === null) {
                setDemandsError(true);
                setDemandsLoading(false);
                return;
            }

            // One entry per company + role (newest first), so repeated
            // postings of the same role don't crowd the list.
            const seen = new Set();
            const merged = [];
            [...(todayRows || []), ...(earlierRows || [])].forEach((row) => {
                if (!row?.id || row.active === false) return;
                const key = demandGroupKey(row);
                if (seen.has(key)) return;
                seen.add(key);
                merged.push(row);
            });

            setOpenDemands(merged.slice(0, MAX_OPEN_DEMANDS));
            setDemandsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, []);


    // Which input the prompt box is showing: "keyword" or "jd". Pure UI —
    // switching tabs never clears keyword or JD input. (searchMode below is
    // different: it records which search produced the current results.)
    const [inputMode, setInputMode] = useState("keyword");

    const [showContact, setShowContact] = useState(false);

    /* ── JD-driven search — scratch-pad state for the JD upload/paste
       flow. The parsed result is kept separately in parsedJdFilters below. ── */
    const [jdText, setJdText] = useState("");
    const [jdFile, setJdFile] = useState(null);
    const [jdUrl, setJdUrl] = useState(null);
    const [jdUploading, setJdUploading] = useState(false);
    // JD image (+ → Add Image): uploaded like the PDF, OCR'd on the server.
    const [jdImageFile, setJdImageFile] = useState(null);
    const [jdImageUrl, setJdImageUrl] = useState(null);
    const [jdImagePreview, setJdImagePreview] = useState(null);
    const [jdImageUploading, setJdImageUploading] = useState(false);
    const jdImageInputRef = useRef(null);
    const [jdParsing, setJdParsing] = useState(false);

    // Which search produced the results currently on screen:
    // null (none yet), "keyword", or "jd". Pagination uses this to pick
    // the matching filter snapshot, so keyword and JD searches never
    // mix their filters. Cleared by Clear.
    const [searchMode, setSearchMode] = useState(null);

    // Parsed keyword filters (from /coffeee-plus-parse-keywords). Kept
    // completely separate from parsedJdFilters below.
    const [parsedKeywordFilters, setParsedKeywordFilters] = useState(null);
    const [keywordParsing, setKeywordParsing] = useState(false);

    // Same stale-response guard as searchIdRef, but for the parse step:
    // a Clear or a newer keyword search while parsing makes the older
    // parse result get ignored instead of triggering a search.
    const keywordParseIdRef = useRef(0);
    // Same stale-response guard for JD parse (Clear / a newer JD search
    // must not apply an older parse).
    const jdParseIdRef = useRef(0);
    // query key -> parsed keyword filters, for this page session.
    const parsedQueryCacheRef = useRef(new Map());
    const resultsAnchorRef = useRef(null);
    // Which pop-over is open: null | "plus" | "recent" | "demands" | "refine".
    // One at a time; outside click or Escape closes it. All closed by default.
    const [openPanel, setOpenPanel] = useState(null);
    const plusRef = useRef(null);
    const recentRef = useRef(null);
    const demandsRef = useRef(null);
    const refineRef = useRef(null);
    // Hidden PDF picker opened from + → Upload JD / Document.
    const jdFileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);

    // Refine: the filters currently APPLIED to the results, and the draft being
    // edited in the Refine panel. Both are reset whenever a new search runs.
    const [refineApplied, setRefineApplied] = useState(emptyRefine);
    const [refineDraft, setRefineDraft] = useState(emptyRefine);
    // Text typed in the Refine location box but not yet turned into a tag
    // (antd tags only commit on Enter / comma). Applied together with the tags.
    const [refineLocationText, setRefineLocationText] = useState("");

    // Parsed JD values live ONLY here. Read by pagination (via
    // getActiveSearch) whenever searchMode is "jd".
    const [parsedJdFilters, setParsedJdFilters] = useState(null);

    // Temporary, frontend-only "reveal" state for phone/email on the card.
    // Independent Sets so a phone and an email can each be revealed on their
    // own. No API call: candidate.primary_no/email are already present in the
    // search result data; this only toggles whether the card shows them. The
    // card's "View Contact" button reveals / hides both together.
    const [revealedPhoneIds, setRevealedPhoneIds] = useState(() => new Set());
    const [revealedEmailIds, setRevealedEmailIds] = useState(() => new Set());

    const revealPhone = (candidateId) => {
        if (!candidateId) return;
        setRevealedPhoneIds((prev) => new Set(prev).add(candidateId));
    };

    const revealEmail = (candidateId) => {
        if (!candidateId) return;
        setRevealedEmailIds((prev) => new Set(prev).add(candidateId));
    };

    const isContactRevealed = (candidateId) =>
        revealedPhoneIds.has(candidateId) || revealedEmailIds.has(candidateId);

    const toggleContact = (candidateId) => {
        if (!candidateId) return;
        if (isContactRevealed(candidateId)) {
            setRevealedPhoneIds((prev) => {
                const next = new Set(prev);
                next.delete(candidateId);
                return next;
            });
            setRevealedEmailIds((prev) => {
                const next = new Set(prev);
                next.delete(candidateId);
                return next;
            });
        } else {
            revealPhone(candidateId);
            revealEmail(candidateId);
        }
    };

    /* ── Results ── */
    const [candidates, setCandidates] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // Detail modal. `selectedCandidate` is the lightweight row from the
    // search results — set immediately so the modal opens with a header
    // right away. `candidateDetail` is the richer payload from the new
    // GET /demand/coffeee-plus-candidate/:candidateId endpoint, fetched
    // separately and populated once it resolves.
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [candidateDetail, setCandidateDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    // What produced the current results (see searchSourceToAiPayload).
    const [activeSearchSource, setActiveSearchSource] = useState(null);
    // "Generate AI score" results kept per candidate + search for
    // this page session, so reopening a popup doesn't need another call.
    const aiResultsRef = useRef(new Map());
    // Keys of AI-score requests currently running (card and popup share this),
    // so the same candidate + search can never be requested twice at once.
    const aiInFlightRef = useRef(new Set());
    // Bumped whenever a score lands in aiResultsRef, so the cards re-render
    // and show it even if the popup was closed while it was generating.
    const [, setAiResultsVersion] = useState(0);
    const [aiState, setAiState] = useState({ key: null, loading: false, error: null, result: null });

    // Card "Fitment Analysis": the generated AI score for this candidate
    // against the current search, or null (shown as "—") if it hasn't been
    // generated yet.
    const getGeneratedAiScoreLabel = (candidate) => {
        const key = aiScoreKey(candidate?.id, activeSearchSource);
        const result = key ? aiResultsRef.current.get(key) : null;
        return result ? toPercentLabel(result.overall_score) : null;
    };
    const [downloadingReport, setDownloadingReport] = useState(false);

    const openCandidateDetails = async (candidate) => {
        setSelectedCandidate(candidate);
        setShowContact(false);
        const key = aiScoreKey(candidate?.id, activeSearchSource);
        setAiState({
            key,
            // keep the spinner if a card-triggered request is still running
            loading: Boolean(key && aiInFlightRef.current.has(key)),
            error: null,
            result: (key && aiResultsRef.current.get(key)) || null,
        });
        setCandidateDetail(null);
        setDetailError(null);
        setDetailLoading(true);

        try {
            const res = await functionService.getCoffeeePlusCandidateDetails(candidate.id);
            if (res?.status) {
                setCandidateDetail(res.response?.data?.data || null);
            } else {
                setDetailError(
                    res?.error?.response?.data?.meta?.message ||
                        res?.error?.response?.data?.error ||
                        "Failed to load candidate details."
                );
            }
        } catch (err) {
            setDetailError("Failed to load candidate details.");
        } finally {
            setDetailLoading(false);
        }
    };

    // AI score of a candidate against the current search. Used by the card's
    // "Generate AI Score" button and by the popup's button: same API call, same
    // loading / result / error state. The backend returns the score without
    // saving it (see generateCoffeeePlusAiScore).
    const generateAiScoreForCandidate = async (candidate = selectedCandidate, { notifyErrors = false } = {}) => {
        const candidateId = candidate?.id;
        const key = aiScoreKey(candidateId, activeSearchSource);
        if (!key || aiState.loading || aiInFlightRef.current.has(key)) return;

        aiInFlightRef.current.add(key);
        setAiState({ key, loading: true, error: null, result: null });
        try {
            const res = await functionService.getCoffeeePlusAiScore({
                candidate_id: candidateId,
                ...searchSourceToAiPayload(activeSearchSource),
            });
            if (res?.status) {
                const result = res.response?.data?.data || null;
                aiResultsRef.current.set(key, result);
                setAiResultsVersion((v) => v + 1);
                setAiState((prev) => (prev.key === key ? { key, loading: false, error: null, result } : prev));
            } else {
                const msg =
                    res?.error?.response?.data?.meta?.message || "Couldn't generate the AI score. Please try again.";
                setAiState((prev) => (prev.key === key ? { key, loading: false, error: msg, result: null } : prev));
                // From the card there is no popup to show the error in.
                if (notifyErrors) message.error(msg);
            }
        } catch (err) {
            const msg = "Couldn't generate the AI score. Please try again.";
            setAiState((prev) =>
                prev.key === key ? { key, loading: false, error: msg, result: null } : prev
            );
            if (notifyErrors) message.error(msg);
        } finally {
            aiInFlightRef.current.delete(key);
        }
    };

    // Existing report download (GET demand/download-assessment-report/:id).
    const downloadAssessmentReport = async (assessmentId, candidateName) => {
        if (!assessmentId || downloadingReport) return;
        setDownloadingReport(true);
        try {
            const data = await functionService.getReportWithResume(
                `demand/download-assessment-report/${assessmentId}`
            );
            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = `${safeFileName(candidateName)}-assessment-report.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            message.error("Couldn't download the assessment report. Please try again.");
        } finally {
            setDownloadingReport(false);
        }
    };

    // Closing the modal only clears this local UI state — it never
    // touches candidates/total/filters, so the search results underneath
    // are exactly as they were.
    const closeCandidateDetails = () => {
        setSelectedCandidate(null);
        setCandidateDetail(null);
        setDetailError(null);
        setDetailLoading(false);
    };

    /* ── Pagination — backend-driven. `candidates` holds only the
       current page's rows (as returned by the API); `total` drives the
       page-count math below. ── */
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Guards against a slow/stale search response overwriting a more
    // recent one (e.g. user clears or re-searches before the first
    // request resolves) — same ref-guard convention used elsewhere in
    // this app for in-flight async requests.
    const searchIdRef = useRef(0);

    /* ─────────────────────────────────────────────
       API — intentionally isolated: this is the only place that calls
       functionService.getCoffeeePlusCandidates. Returns the inner
       { data, total } payload, or throws on failure.
    ───────────────────────────────────────────── */
    const fetchCandidates = async (payload) => {
        const result = await functionService.getCoffeeePlusCandidates(payload);

        if (!result?.status) {
            throw result?.error || new Error("Failed to fetch candidates");
        }

        return result.response?.data?.data;
    };

    // Shared by every search and by pagination (page / page-size changes) and
    // by Refine.
    //
    // `filters`, `isJdSearchFlag` and `refine` are passed in explicitly rather
    // than read from state: immediately after a parse, this gets called in the
    // same synchronous handler that just called the state setters, and
    // those updates haven't committed yet at that point (classic
    // stale-closure risk) — so the caller always hands over the exact
    // values to use for this call.
    const runCandidateSearch = async (page, limit, filters, isJdSearchFlag, refine) => {
        const payload = {
            page,
            limit,
            ctc_min: filters?.ctc_min ?? null,
            ctc_max: filters?.ctc_max ?? null,
            role_name: filters?.role_name ?? null,
            skills: filters?.skills || [],
            experience_min: filters?.experience_min ?? null,
            experience_max: filters?.experience_max ?? null,
            location: normalizeSearchLocations(filters?.location),
            // Both modes compare against the candidate's EXPECTED CTC. A JD's
            // ctc_min/max is the offered range, so it is sent as the budget.
            budget_min: filters?.budget_min ?? filters?.ctc_min ?? null,
            budget_max: filters?.budget_max ?? filters?.ctc_max ?? null,
            budget_min_strict: filters?.budget_min_strict === true,
            budget_max_strict: filters?.budget_max_strict === true,
            is_jd_search: isJdSearchFlag,
            // "keyword" and "jd" both use the same scoring / eligibility /
            // ranking pipeline on the backend.
            search_mode: isJdSearchFlag ? "jd" : "keyword",
            // Extra filters over the original result set (null = none). The
            // original criteria above are always sent unchanged.
            refine: refineToPayload(refine),
        };

        const thisSearchId = ++searchIdRef.current;

        setLoading(true);
        setHasSearched(true);

        try {
            const responseData = await fetchCandidates(payload);
            if (searchIdRef.current !== thisSearchId) return; // superseded by a newer request
            setCandidates(responseData?.data || []);
            setTotal(responseData?.total || 0);
        } catch (err) {
            if (searchIdRef.current !== thisSearchId) return;
            message.error("Something went wrong while searching candidates. Please try again.");
            setCandidates([]);
            setTotal(0);
        } finally {
            if (searchIdRef.current === thisSearchId) {
                setLoading(false);
            }
        }
    };

    // Returns the filter snapshot for whichever search produced the
    // current results, or null if none. Used by pagination and Refine so a
    // page change or a refinement re-runs exactly the same original search.
    const getActiveSearch = () => {
        if (searchMode === "jd" && parsedJdFilters) {
            return { filters: parsedJdFilters, isJdSearchFlag: true };
        }
        if (searchMode === "keyword" && parsedKeywordFilters) {
            return { filters: parsedKeywordFilters, isJdSearchFlag: false };
        }
        return null;
    };

    // Keyword search — the arrow button and Enter key on the Keywords tab.
    // 1) Backend parses the text into structured filters (one LLM call).
    // 2) Those filters are stored ONLY in parsedKeywordFilters.
    // 3) Candidate search runs with is_jd_search: false.
    // `textOverride` lets a recent search re-run immediately, before the
    // keywordQuery state update has committed.
    const handleKeywordSearch = async (textOverride) => {
        if (loading || jdParsing || keywordParsing) return; // duplicate-request guard

        const trimmed = (typeof textOverride === "string" ? textOverride : keywordQuery).trim();
        if (!trimmed) {
            message.warning("Enter a few keywords, e.g. React Bangalore 3-5yrs Node 15LPA.");
            return;
        }

        const thisParseId = ++keywordParseIdRef.current;
        const queryKey = normalizeQueryKey(trimmed);

        // Already parsed this query (earlier in this session, or saved with a
        // recent search)? Reuse the filters — no parse request, no LLM call.
        // They are re-normalised, and the backend re-validates them anyway.
        const known =
            parsedQueryCacheRef.current.get(queryKey) || findSavedFilters(recentSearches, trimmed);
        if (known) {
            const cachedFilters = buildKeywordFilters(known);
            if (hasAnyKeywordCriteria(cachedFilters)) {
                setKeywordParsing(false);
                applyKeywordSearch(trimmed, queryKey, cachedFilters);
                return;
            }
        }

        let parsed = null;

        setKeywordParsing(true);
        try {
            const result = await functionService.parseCoffeeePlusKeywords({ search_text: trimmed });
            if (keywordParseIdRef.current !== thisParseId) return; // cleared or superseded

            if (result?.status) {
                parsed = result?.response?.data?.data || null;
            } else {
                const status = result?.error?.response?.status;
                // 400s are about the query itself (e.g. "5-3yrs") and are
                // written for users; anything else gets a generic message.
                message.error(
                    status === 400
                        ? result?.error?.response?.data?.meta?.message || "Please check your search keywords."
                        : "Couldn't understand that search right now. Please try again."
                );
            }
        } catch (err) {
            if (keywordParseIdRef.current !== thisParseId) return;
            message.error("Couldn't understand that search right now. Please try again.");
        } finally {
            if (keywordParseIdRef.current === thisParseId) {
                setKeywordParsing(false);
            }
        }

        if (!parsed) return;

        const keywordFilters = buildKeywordFilters(parsed);

        if (!hasAnyKeywordCriteria(keywordFilters)) {
            message.warning(
                "Couldn't find a role, skill, city, experience or budget in that search. Try e.g. React Bangalore 3-5yrs 15LPA."
            );
            return;
        }

        applyKeywordSearch(trimmed, queryKey, keywordFilters);
    };

    const hasAnyKeywordCriteria = (f) =>
        Boolean(
            f.role_name ||
                f.skills.length > 0 ||
                normalizeSearchLocations(f.location).length > 0 ||
                f.experience_min !== null ||
                f.experience_max !== null ||
                f.budget_min !== null ||
                f.budget_max !== null
        );

    // Shared by fresh and cached keyword searches: remember the query with its
    // filters, then run the candidate search (a DB query, never an LLM call).
    // A new search starts without any Refine filters.
    const applyKeywordSearch = (trimmed, queryKey, keywordFilters) => {
        parsedQueryCacheRef.current.set(queryKey, keywordFilters);

        // Remember it (only searches that produced usable filters).
        setRecentSearches((prev) => {
            const next = addToRecentSearches(prev, trimmed, keywordFilters);
            saveRecentSearches(next);
            return next;
        });

        setActiveDemandLabel(null);
        setActiveSearchSource({ type: "keyword", value: keywordFilters, label: `Keyword search: ${trimmed}` });
        setParsedKeywordFilters(keywordFilters);
        setSearchMode("keyword");
        setRefineApplied(emptyRefine());
        setRefineDraft(emptyRefine());
        setCurrentPage(1);
        runCandidateSearch(1, pageSize, keywordFilters, false, emptyRefine());
    };

    // JD search ONLY — the "Search with JD" button. Parses the existing
    // JD upload/paste input, keeps the result ONLY in parsedJdFilters,
    // then searches directly with is_jd_search: true. No separate
    // "review the filters" step. Never reads or writes keyword state.
    // `override` = { payload, label } when the JD comes from an open demand
    // instead of the upload/paste inputs (those inputs are left untouched).
    const handleSearchWithJd = async (override) => {
        if (loading || jdParsing || keywordParsing) return; // duplicate-request guard, shared with keyword search

        const fromDemand = override && override.payload ? override : null;
        const trimmedJdText = jdText.trim();

        let parsePayload = null;
        if (fromDemand) {
            parsePayload = fromDemand.payload;
        } else if (trimmedJdText) {
            if (trimmedJdText.length < 250) {
                message.warning("JD text must be at least 250 characters long.");
                return;
            }
            parsePayload = { jd_text: trimmedJdText };
        } else if (jdUrl) {
            // Same priority as before: entered text wins over an uploaded PDF.
            parsePayload = { jd_url: jdUrl };
        } else if (jdImageUrl) {
            // Then an uploaded JD image (read with OCR on the server).
            parsePayload = { jd_image_url: jdImageUrl };
        } else {
            message.warning("Please upload a JD (PDF or image) or enter JD text.");
            return;
        }

        let parsed = null;
        const thisParseId = ++jdParseIdRef.current;
        setJdParsing(true);
        try {
            const result = await functionService.parseCoffeeePlusJD(parsePayload);
            if (jdParseIdRef.current !== thisParseId) return;
            if (result?.status) {
                parsed = result?.response?.data?.data || {};
            } else {
                message.error(
                    result?.error?.response?.data?.meta?.message ||
                        result?.error?.response?.data?.error ||
                        "Failed to parse JD. Please try again."
                );
            }
        } catch (err) {
            if (jdParseIdRef.current !== thisParseId) return;
            message.error("Failed to parse JD. Please try again.");
        } finally {
            if (jdParseIdRef.current === thisParseId) {
                setJdParsing(false);
            }
        }

        // Parsing failed or was superseded — stop here. JD input is
        // untouched, and no candidate search is performed.
        if (jdParseIdRef.current !== thisParseId) return;
        if (!parsed) return;

        const jdFilters = buildJdFilters(parsed);

        setParsedJdFilters(jdFilters);
        setActiveDemandLabel(fromDemand ? fromDemand.label : null);
        setActiveSearchSource(
            parsePayload.jd_text
                ? { type: "jd_text", value: parsePayload.jd_text, label: "Pasted job description" }
                : parsePayload.jd_image_url
                ? { type: "jd_image", value: parsePayload.jd_image_url, label: jdImageFile?.name || "Uploaded JD image" }
                : {
                      type: "jd_url",
                      value: parsePayload.jd_url,
                      label: fromDemand ? `JD of ${fromDemand.label}` : jdFile?.name || "Uploaded job description",
                  }
        );
        setSearchMode("jd");
        setRefineApplied(emptyRefine());
        setRefineDraft(emptyRefine());
        setCurrentPage(1);
        runCandidateSearch(1, pageSize, jdFilters, true, emptyRefine());
    };

    const handlePageChange = (page) => {
        if (loading) return;
        const active = getActiveSearch();
        if (!active) return;
        setCurrentPage(page);
        runCandidateSearch(page, pageSize, active.filters, active.isJdSearchFlag, refineApplied);
    };

    const handlePageSizeChange = (size) => {
        if (loading) return;
        const active = getActiveSearch();
        if (!active) return;
        setPageSize(size);
        setCurrentPage(1);
        runCandidateSearch(1, size, active.filters, active.isJdSearchFlag, refineApplied);
    };

    const hasNumeric = (value) => value !== null && value !== undefined;

    const hasAnyRefineCriteria = (f) =>
        Boolean(
            f?.role_name ||
                (f?.skills && f.skills.length > 0) ||
                normalizeSearchLocations(f?.location).length > 0 ||
                hasNumeric(f?.experience_min) ||
                hasNumeric(f?.experience_max) ||
                hasNumeric(f?.budget_min) ||
                hasNumeric(f?.budget_max) ||
                hasNumeric(f?.ctc_min) ||
                hasNumeric(f?.ctc_max)
        );

    const isBusy = loading || jdParsing || keywordParsing;

    // Used by the "Searching for" chips (they change the ORIGINAL search, so
    // any Refine filters are cleared and the search restarts from page 1).
    const commitFilters = (next, mode) => {
        if (mode === "jd") {
            setParsedJdFilters(next);
        } else {
            setParsedKeywordFilters(next);
            setActiveSearchSource((prev) =>
                prev?.type === "keyword" ? { ...prev, value: next, label: "Keyword search (refined)" } : prev
            );
        }
        setRefineApplied(emptyRefine());
        setRefineDraft(emptyRefine());
        setCurrentPage(1);
        runCandidateSearch(1, pageSize, next, mode === "jd", emptyRefine());
    };

    /* ── Refine: applies extra filters to the SAME original search ──
       Nothing about the original search changes: the parsed filters, the
       search source (AI score), the recent searches and the scores/ranking all
       stay as they are. The original criteria are re-sent unchanged together
       with the refine filters, so the backend can filter the full eligible
       result set (and return correct totals and pages). */
    const applyRefine = (next) => {
        if (isBusy) return;
        const active = getActiveSearch();
        if (!active) return;

        if (
            next.experience_min !== null &&
            next.experience_max !== null &&
            next.experience_min > next.experience_max
        ) {
            message.warning("Experience min can't be greater than max.");
            return;
        }
        if (next.budget_min !== null && next.budget_max !== null && next.budget_min > next.budget_max) {
            message.warning("Budget min can't be greater than max.");
            return;
        }

        setRefineApplied(next);
        setRefineDraft(next);
        setCurrentPage(1);
        runCandidateSearch(1, pageSize, active.filters, active.isJdSearchFlag, next);
    };

    const clearRefine = () => {
        setRefineLocationText("");
        applyRefine(emptyRefine());
    };

    // Apply button: include a location that was typed but not confirmed with Enter.
    const submitRefineDraft = () => {
        const pending = refineLocationText.trim();
        const next = pending
            ? { ...refineDraft, locations: normalizeSearchLocations([...refineDraft.locations, ...pending.split(",")]) }
            : refineDraft;
        setRefineLocationText("");
        applyRefine(next);
    };

    const patchRefineDraft = (patch) => setRefineDraft((prev) => ({ ...prev, ...patch }));

    const refineDirty = useMemo(
        () => JSON.stringify(refineDraft) !== JSON.stringify(refineApplied),
        [refineDraft, refineApplied]
    );

    const refineActive = isRefineActive(refineApplied);

    // Removable chips for the applied Refine filters (removing one keeps the others).
    const refineChips = (() => {
        const chips = [];
        refineApplied.locations.forEach((city) =>
            chips.push({
                key: `rloc-${city}`,
                label: city,
                remove: (r) => ({ ...r, locations: r.locations.filter((l) => l !== city) }),
            })
        );
        const exp = formatExperienceRange(refineApplied.experience_min, refineApplied.experience_max);
        if (exp) {
            chips.push({
                key: "rexp",
                label: exp,
                remove: (r) => ({ ...r, experience_min: null, experience_max: null }),
            });
        }
        const budget = formatBudgetRange(refineApplied.budget_min, refineApplied.budget_max, false, false);
        if (budget) {
            chips.push({
                key: "rbudget",
                label: budget,
                remove: (r) => ({ ...r, budget_min: null, budget_max: null }),
            });
        }
        refineApplied.assessment.forEach((value) =>
            chips.push({
                key: `rassess-${value}`,
                label: `Assessment: ${optionLabel(ASSESSMENT_REFINE_OPTIONS, value)}`,
                remove: (r) => ({ ...r, assessment: r.assessment.filter((v) => v !== value) }),
            })
        );
        refineApplied.interview.forEach((value) =>
            chips.push({
                key: `rint-${value}`,
                label: `Interview: ${optionLabel(INTERVIEW_REFINE_OPTIONS, value)}`,
                remove: (r) => ({ ...r, interview: r.interview.filter((v) => v !== value) }),
            })
        );
        return chips;
    })();

    const removeRefineChip = (chip) => {
        if (isBusy) return;
        applyRefine(chip.remove(refineApplied));
    };

    const removeActiveChip = (chip) => {
        if (isBusy || !searchMode) return;
        const current = searchMode === "jd" ? parsedJdFilters : parsedKeywordFilters;
        if (!current) return;
        const next = {
            ...current,
            skills: Array.isArray(current.skills) ? [...current.skills] : [],
        };
        if (chip.key === "role") next.role_name = null;
        else if (chip.key.startsWith("location-")) {
            // Remove only this city; the others stay.
            const city = chip.key.slice("location-".length);
            next.location = normalizeSearchLocations(current.location).filter((l) => l !== city);
        }
        else if (chip.key === "exp") {
            next.experience_min = null;
            next.experience_max = null;
        } else if (chip.key === "budget") {
            next.budget_min = null;
            next.budget_max = null;
            next.budget_min_strict = false;
            next.budget_max_strict = false;
        } else if (chip.key === "ctc") {
            next.ctc_min = null;
            next.ctc_max = null;
        } else if (chip.key.startsWith("skill-")) {
            const skill = chip.key.slice("skill-".length);
            next.skills = next.skills.filter((s) => s !== skill);
        }
        if (!hasAnyRefineCriteria(next)) {
            message.warning("Keep at least one filter — role, skill, city, experience or pay.");
            return;
        }
        commitFilters(next, searchMode);
    };

    const handleClear = () => {
        searchIdRef.current += 1; // invalidate any in-flight search

        // Keyword search
        keywordParseIdRef.current += 1; // ignore any in-flight keyword parse
        setKeywordParsing(false);
        setKeywordQuery("");
        setParsedKeywordFilters(null);

        // Results
        setCandidates([]);
        setTotal(0);
        setHasSearched(false);
        setLoading(false);
        setCurrentPage(1);
        setSearchMode(null);
        setOpenPanel(null);
        setRevealedPhoneIds(new Set());
        setRevealedEmailIds(new Set());
        setRefineApplied(emptyRefine());
        setRefineDraft(emptyRefine());
        setDemandQuery("");
        setDemandLocation(null);
        setDemandExp(null);

        // JD scratch-pad — cleared alongside everything else on Clear,
        // consistent with the rest of this reset. Does not trigger a search.
        jdParseIdRef.current += 1;
        setJdParsing(false);
        setJdText("");
        setJdFile(null);
        setJdUrl(null);
        clearJdImage();
        setInputMode("keyword");
        setParsedJdFilters(null);
        setActiveDemandLabel(null);
        setActiveSearchSource(null);
        aiResultsRef.current = new Map();
        setAiResultsVersion((v) => v + 1);
    };

    /* ─────────────────────────────────────────────
       JD upload — uploads the PDF and stores its URL in jdUrl. Parsing
       and searching happen only when "Search with JD" is clicked
       (handleSearchWithJd above).
    ───────────────────────────────────────────── */
    // Called from + → Upload JD / Document (hidden file input) and from
    // dropping a PDF onto the composer — same validation and upload as before.
    const handleJdUpload = async (file) => {
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            message.error("Only PDF files are allowed");
            return false;
        }
        // Same 2MB limit already used for resume/misc-file uploads elsewhere
        // in this app (DemandForm/CallerForm), reused here for consistency.
        if (file.size > 2 * 1024 * 1024) {
            message.error("File size can't be more than 2MB");
            return false;
        }

        setJdUploading(true);
        try {
            // Reuses the existing generic upload endpoint — no new upload
            // library, no frontend PDF/OCR logic. The backend's existing
            // JD extraction/OCR runs later, inside parseCoffeeePlusJD.
            const res = await functionService.uploadFile(file);
            if (res.status) {
                setJdUrl(res.response?.data?.location || null);
                setJdFile(file);
            } else {
                message.error("Failed to upload JD. Please try again.");
            }
        } catch (err) {
            message.error("Failed to upload JD. Please try again.");
        } finally {
            setJdUploading(false);
        }

        return false; // prevent antd's own auto-upload — we already handled it above
    };

    const clearJdFile = () => {
        setJdFile(null);
        setJdUrl(null);
    };

    // JD image: same upload endpoint and 2MB limit as the PDF. Text is read
    // from it on the server (OCR) when the search runs.
    const handleJdImageUpload = async (file) => {
        if (!isJdImageFile(file)) {
            message.error("Only PNG, JPG or WEBP images are allowed");
            return;
        }
        if (file.size === 0) {
            message.error("That image file is empty");
            return;
        }
        if (file.size > JD_MAX_BYTES) {
            message.error("Image size can't be more than 2MB");
            return;
        }
        setJdImageUploading(true);
        try {
            const res = await functionService.uploadFile(file);
            const location = res?.response?.data?.location;
            if (res?.status && location) {
                setJdImageUrl(location);
                setJdImageFile(file);
                setJdImagePreview(URL.createObjectURL(file));
            } else {
                message.error("Failed to upload image. Please try again.");
            }
        } catch (err) {
            message.error("Failed to upload image. Please try again.");
        } finally {
            setJdImageUploading(false);
        }
    };

    const clearJdImage = () => {
        setJdImageFile(null);
        setJdImageUrl(null);
        setJdImagePreview(null);
    };

    // Free the local preview URL when it changes or the page closes.
    useEffect(() => {
        if (!jdImagePreview) return undefined;
        return () => URL.revokeObjectURL(jdImagePreview);
    }, [jdImagePreview]);

    // The prompt box's single submit arrow runs whichever search the
    // active tab represents.
    const handleSubmit = () => {
        if (inputMode === "jd") {
            handleSearchWithJd();
        } else {
            handleKeywordSearch();
        }
    };

    /* ── Composer: + menu, pop-overs, drag-and-drop ── */
    const togglePanel = (name) => setOpenPanel((current) => (current === name ? null : name));

    // Outside click / Escape closes whichever pop-over is open.
    useEffect(() => {
        if (!openPanel) return undefined;
        const anchors = { plus: plusRef, recent: recentRef, demands: demandsRef, refine: refineRef };
        const onPointerDown = (event) => {
            const anchor = anchors[openPanel]?.current;
            if (anchor && anchor.contains(event.target)) return;
            // Ant Design renders some pop-ups (messages, select lists) at <body>.
            if (event.target?.closest?.(".ant-select-dropdown, .ant-message")) return;
            setOpenPanel(null);
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setOpenPanel(null);
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [openPanel]);

    // After a mode switch the textarea re-renders; focus it on the next tick.
    const focusComposer = () => {
        setTimeout(() => keywordInputRef.current?.focus(), 0);
    };

    const switchMode = (mode) => {
        setInputMode(mode);
        focusComposer();
    };

    // The composer has no visible Keywords / Job description tabs. JD mode is
    // entered from the + menu (or by pasting a long JD) and shown as a chip
    // above the input; removing the last JD input goes back to keywords.
    const removeJdFileChip = () => {
        clearJdFile();
        if (!jdText.trim() && !jdImageFile) switchMode("keyword");
    };

    const removeJdImageChip = () => {
        clearJdImage();
        if (!jdText.trim() && !jdFile) switchMode("keyword");
    };

    const exitJdTextMode = () => switchMode("keyword");

    // A pasted JD is longer than the keyword limit: switch to JD text instead
    // of letting the keyword box cut it off.
    const onComposerPaste = (event) => {
        if (inputMode !== "keyword") return;
        const pasted = event.clipboardData?.getData("text") || "";
        if (pasted.trim().length <= KEYWORD_QUERY_MAX_LENGTH) return;
        event.preventDefault();
        setJdText(pasted.trim());
        switchMode("jd");
    };

    const choosePlusAction = (action) => {
        setOpenPanel(null);
        if (action === "jd-file") {
            jdFileInputRef.current?.click();
        } else if (action === "image") {
            jdImageInputRef.current?.click();
        } else if (action === "jd-text") {
            switchMode("jd");
        }
    };

    const onJdFileChosen = (event) => {
        const file = event.target.files?.[0];
        event.target.value = ""; // allow picking the same file again
        if (!file) return;
        setInputMode("jd");
        handleJdUpload(file);
    };

    const onJdImageChosen = (event) => {
        const file = event.target.files?.[0];
        event.target.value = ""; // allow picking the same file again
        if (!file) return;
        setInputMode("jd");
        handleJdImageUpload(file);
    };

    const onComposerDrop = (event) => {
        event.preventDefault();
        setDragActive(false);
        const file = event.dataTransfer?.files?.[0];
        if (!file) return;
        setInputMode("jd");
        // Images go to the image upload; anything else gets the PDF checks.
        if (isJdImageFile(file)) handleJdImageUpload(file);
        else handleJdUpload(file);
    };

    // Recent search: fill the box and run it straight away, using the filters
    // saved with it — no parse request and no LLM call.
    const runRecentSearch = (query) => {
        if (isBusy) return;
        setOpenPanel(null);
        setInputMode("keyword");
        setKeywordQuery(query);
        handleKeywordSearch(query);
    };

    const clearRecentSearches = () => {
        setRecentSearches([]);
        saveRecentSearches([]);
    };

    // Open demand: JD search using the JD attached to that demand.
    const searchOpenDemand = (demand) => {
        if (isBusy) return;
        const jdUrl = typeof demand?.job_desc === "string" ? demand.job_desc.trim() : "";
        if (!jdUrl) {
            message.warning("This demand has no JD attached yet.");
            return;
        }
        const label = [demand.company_name, demand.role_title].filter(Boolean).join(" — ");
        setOpenPanel(null);
        handleSearchWithJd({ payload: { jd_url: jdUrl }, label });
    };

    // Pure — normalizes the keyword-parser response into the filter
    // snapshot used by runCandidateSearch and pagination. The backend has
    // already validated it; this is a defensive second pass.
    const buildKeywordFilters = (parsed) => ({
        role_name: typeof parsed?.role_name === "string" && parsed.role_name.trim() ? parsed.role_name.trim() : null,
        skills: dedupeCaseInsensitive(
            (Array.isArray(parsed?.skills) ? parsed.skills : [])
                .map((s) => (typeof s === "string" ? s.trim() : ""))
                .filter(Boolean)
        ),
        location: normalizeSearchLocations(parsed?.location),
        experience_min: normalizeNumberOrNull(parsed?.experience_min),
        experience_max: normalizeNumberOrNull(parsed?.experience_max),
        budget_min: normalizeNumberOrNull(parsed?.budget_min),
        budget_max: normalizeNumberOrNull(parsed?.budget_max),
        budget_min_strict: parsed?.budget_min_strict === true,
        budget_max_strict: parsed?.budget_max_strict === true,
        // Keyword search never sets current-CTC filters.
        ctc_min: null,
        ctc_max: null,
    });

    // Pure — returns the normalized JD-search filter object, touches no
    // state. The parsed values are sent to the backend exactly as the
    // parser returned them (trimmed/de-duped/typed), letting the backend's
    // own scoring and eligibility rules do that work. The JD's location (if
    // it states one) is carried through, because location is mandatory.
    const buildJdFilters = (parsed) => ({
        role_name: typeof parsed?.role_name === "string" && parsed.role_name.trim() ? parsed.role_name.trim() : null,
        skills: dedupeCaseInsensitive(
            (Array.isArray(parsed?.skills) ? parsed.skills : [])
                .map((s) => (typeof s === "string" ? s.trim() : ""))
                .filter(Boolean)
        ),
        location: normalizeSearchLocations(parsed?.location),
        experience_min: normalizeNumberOrNull(parsed?.experience_min),
        experience_max: normalizeNumberOrNull(parsed?.experience_max),
        ctc_min: normalizeNumberOrNull(parsed?.ctc_min),
        ctc_max: normalizeNumberOrNull(parsed?.ctc_max),
    });

    /* ── Pagination derived state ── */
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const pageStart = (currentPage - 1) * pageSize;
    const itemsToDisplay = candidates;

    const showTable = hasSearched && !loading && candidates.length > 0;
    const showZeroResults = hasSearched && !loading && candidates.length === 0;

    useEffect(() => {
        if (!hasSearched || loading || currentPage === 1) return;
        resultsAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [hasSearched, loading, currentPage]);


    // Chips describing the ORIGINAL search currently applied to the results.
    // Every chip is sent to the backend; clicking one removes that part
    // and re-runs the search.
    const activeChips = (() => {
        const f = searchMode === "jd" ? parsedJdFilters : searchMode === "keyword" ? parsedKeywordFilters : null;
        if (!f) return [];
        const chips = [];
        if (f.role_name) chips.push({ key: "role", label: f.role_name });
        (f.skills || []).forEach((skill) => chips.push({ key: `skill-${skill}`, label: skill }));
        const exp = formatExperienceRange(f.experience_min, f.experience_max);
        if (exp) chips.push({ key: "exp", label: exp });
        normalizeSearchLocations(f.location).forEach((city) => chips.push({ key: `location-${city}`, label: city }));
        if (searchMode === "jd") {
            const ctc = formatCtcRange(f.ctc_min ?? null, f.ctc_max ?? null);
            if (ctc) chips.push({ key: "ctc", label: ctc });
        } else {
            const budget = formatBudgetRange(f.budget_min, f.budget_max, f.budget_min_strict, f.budget_max_strict);
            if (budget) chips.push({ key: "budget", label: budget });
        }
        return chips;
    })();

    const demandLocations = useMemo(() => {
        const cities = new Set();
        openDemands.forEach((d) => {
            const city = firstDemandCity(d?.recruiter_location);
            if (city) cities.add(city);
        });
        return [...cities].sort((a, b) => a.localeCompare(b));
    }, [openDemands]);

    const filteredOpenDemands = useMemo(() => {
        const q = demandQuery.trim().toLowerCase();
        const bucket = DEMAND_EXP_BUCKETS.find((b) => b.key === demandExp) || null;
        return openDemands.filter((d) => {
            if (demandLocation && firstDemandCity(d?.recruiter_location) !== demandLocation) return false;
            if (bucket && !demandMatchesExpBucket(d, bucket)) return false;
            if (!q) return true;
            const haystack = [
                d?.role_title,
                d?.company_name,
                firstDemandCity(d?.recruiter_location),
                describeDemand(d),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [openDemands, demandQuery, demandLocation, demandExp]);

    const submitDisabled = isBusy || jdUploading || jdImageUploading;

    return (
        <>
            <S.GlobalFontStyle />
            <S.PageWrapper>
                <div style={{ position: "sticky", top: 0, height: "100vh", flexShrink: 0, zIndex: 100 }}>
                    <Sidemenu />
                </div>

                <S.ContentWrap>
                    {/* Full-screen blurred loader while the JD is being parsed —
                        same Loader (and loaderOpen/loaderText props) CallerForm
                        uses for "Fetch Resume Details". */}
                    {jdParsing && (
                        <Loader
                            loaderOpen={true}
                            loaderText={
                                !jdText.trim() && !jdUrl && jdImageUrl
                                    ? "Reading image — extracting filters..."
                                    : "Parsing JD — extracting filters..."
                            }
                        />
                    )}

                    {/* ════ HEADER + TOP-RIGHT BUTTONS ════
                       Recent searches, Open demands (and Refine, after a search)
                       open as pop-overs — closed by default, no reserved space. */}
                    <S.TopBar>
                        <div>
                            <S.HeaderTitle>Candidate Search</S.HeaderTitle>
                            <S.HeaderSubtitle>Find candidates in the Coffeee pool.</S.HeaderSubtitle>
                        </div>

                        <S.TopActions>
                            {hasSearched && (
                                <S.PopoverAnchor ref={refineRef}>
                                    <S.TopButton
                                        type="button"
                                        $active={openPanel === "refine"}
                                        onClick={() => togglePanel("refine")}
                                        aria-expanded={openPanel === "refine"}
                                        aria-haspopup="dialog"
                                        aria-label="Refine results"
                                    >
                                        <SlidersOutlined />
                                        <span className="label">Refine</span>
                                        {(refineActive || refineDirty) && <S.DirtyDot aria-hidden="true" />}
                                    </S.TopButton>
                                    {openPanel === "refine" && (
                                        <S.Popover $width={360} role="dialog" aria-label="Refine results">
                                            <S.PanelHeader>
                                                <S.PanelTitle>
                                                    <SlidersOutlined /> Refine results
                                                </S.PanelTitle>
                                                {refineActive && (
                                                    <S.PanelLink type="button" onClick={clearRefine} disabled={isBusy}>
                                                        Clear all
                                                    </S.PanelLink>
                                                )}
                                            </S.PanelHeader>
                                        <S.RefineGrid>
                                            <div>
                                                <S.RefineLabel>Location</S.RefineLabel>
                                                <S.CompactField>
                                                    <Select
                                                        mode="tags"
                                                        value={refineDraft.locations}
                                                        onChange={(locations) =>
                                                            patchRefineDraft({
                                                                locations: normalizeSearchLocations(locations),
                                                            })
                                                        }
                                                        placeholder="Type a city and press Enter"
                                                        tokenSeparators={[","]}
                                                        open={false}
                                                        searchValue={refineLocationText}
                                                        onSearch={setRefineLocationText}
                                                    />
                                                </S.CompactField>
                                            </div>

                                            <div>
                                                <S.RefineLabel>Experience (yrs)</S.RefineLabel>
                                                <S.RefinePair>
                                                    <S.CompactField>
                                                        <InputNumber
                                                            min={0}
                                                            max={40}
                                                            value={refineDraft.experience_min}
                                                            onChange={(v) => patchRefineDraft({ experience_min: normalizeNumberOrNull(v) })}
                                                            placeholder="From"
                                                            controls={false}
                                                        />
                                                    </S.CompactField>
                                                    <S.RefineSep>to</S.RefineSep>
                                                    <S.CompactField>
                                                        <InputNumber
                                                            min={0}
                                                            max={40}
                                                            value={refineDraft.experience_max}
                                                            onChange={(v) => patchRefineDraft({ experience_max: normalizeNumberOrNull(v) })}
                                                            placeholder="To"
                                                            controls={false}
                                                        />
                                                    </S.CompactField>
                                                </S.RefinePair>
                                            </div>

                                            <div>
                                                <S.RefineLabel>Budget (LPA)</S.RefineLabel>
                                                <S.RefinePair>
                                                    <S.CompactField>
                                                        <InputNumber
                                                            min={0}
                                                            max={200}
                                                            value={refineDraft.budget_min}
                                                            onChange={(v) => patchRefineDraft({ budget_min: normalizeNumberOrNull(v) })}
                                                            placeholder="From"
                                                            controls={false}
                                                        />
                                                    </S.CompactField>
                                                    <S.RefineSep>to</S.RefineSep>
                                                    <S.CompactField>
                                                        <InputNumber
                                                            min={0}
                                                            max={200}
                                                            value={refineDraft.budget_max}
                                                            onChange={(v) => patchRefineDraft({ budget_max: normalizeNumberOrNull(v) })}
                                                            placeholder="To"
                                                            controls={false}
                                                        />
                                                    </S.CompactField>
                                                </S.RefinePair>
                                            </div>

                                            <div>
                                                <S.RefineLabel>Assessment Status</S.RefineLabel>
                                                <S.CompactField>
                                                    <Select
                                                        mode="multiple"
                                                        value={refineDraft.assessment}
                                                        onChange={(assessment) => patchRefineDraft({ assessment })}
                                                        options={ASSESSMENT_REFINE_OPTIONS}
                                                        placeholder="Any"
                                                        allowClear
                                                    />
                                                </S.CompactField>
                                            </div>

                                            <div>
                                                <S.RefineLabel>Interview Status</S.RefineLabel>
                                                <S.CompactField>
                                                    <Select
                                                        mode="multiple"
                                                        value={refineDraft.interview}
                                                        onChange={(interview) => patchRefineDraft({ interview })}
                                                        options={INTERVIEW_REFINE_OPTIONS}
                                                        placeholder="Any"
                                                        allowClear
                                                    />
                                                </S.CompactField>
                                            </div>
                                        </S.RefineGrid>

                                        <S.RefineActions>
                                            <S.RefineReset
                                                type="button"
                                                onClick={clearRefine}
                                                disabled={isBusy || (!refineActive && !refineDirty)}
                                            >
                                                Clear
                                            </S.RefineReset>
                                            <S.RefineApply
                                                type="button"
                                                onClick={submitRefineDraft}
                                                disabled={isBusy || (!refineDirty && !refineLocationText.trim())}
                                            >
                                                <FilterOutlined /> Apply filters
                                            </S.RefineApply>
                                        </S.RefineActions>
                                        </S.Popover>
                                    )}
                                </S.PopoverAnchor>
                            )}

                            <S.PopoverAnchor ref={recentRef}>
                                <S.TopButton
                                    type="button"
                                    $active={openPanel === "recent"}
                                    onClick={() => togglePanel("recent")}
                                    aria-expanded={openPanel === "recent"}
                                    aria-haspopup="dialog"
                                    aria-label="Recent Searches"
                                >
                                    <HistoryOutlined />
                                    <span className="label">Recent Searches</span>
                                </S.TopButton>
                                {openPanel === "recent" && (
                                    <S.Popover $width={340} role="dialog" aria-label="Recent searches">
                                        <S.PanelHeader>
                                            <S.PanelTitle>
                                                <HistoryOutlined /> Recent searches
                                            </S.PanelTitle>
                                            {recentSearches.length > 0 && (
                                                <S.PanelLink type="button" onClick={clearRecentSearches}>
                                                    Clear
                                                </S.PanelLink>
                                            )}
                                        </S.PanelHeader>

                            {recentSearches.length === 0 ? (
                                <S.PanelEmpty>Your keyword searches will show up here.</S.PanelEmpty>
                            ) : (
                                <S.PanelList>
                                {recentSearches.map((item) => (
                                    <S.PanelItem
                                        key={item.query}
                                        type="button"
                                        onClick={() => runRecentSearch(item.query)}
                                        disabled={isBusy}
                                        title={`Search again: ${item.query}`}
                                    >
                                        <S.PanelItemTitle>{item.query}</S.PanelItemTitle>
                                        <S.PanelItemMeta>{formatTimeAgo(item.at)}</S.PanelItemMeta>
                                    </S.PanelItem>
                                ))}
                                </S.PanelList>
                            )}
                                    </S.Popover>
                                )}
                            </S.PopoverAnchor>

                            <S.PopoverAnchor ref={demandsRef}>
                                <S.TopButton
                                    type="button"
                                    $active={openPanel === "demands"}
                                    onClick={() => togglePanel("demands")}
                                    aria-expanded={openPanel === "demands"}
                                    aria-haspopup="dialog"
                                    aria-label="Open Demands"
                                >
                                    <AppstoreOutlined />
                                    <span className="label">Open Demands</span>
                                </S.TopButton>
                                {openPanel === "demands" && (
                                    <S.Popover $width={400} $tall role="dialog" aria-label="Open demands">
                                        <S.PanelHeader>
                                            <S.PanelTitle>
                                                <AppstoreOutlined /> Open demands
                                            </S.PanelTitle>
                                        </S.PanelHeader>

                            {!demandsLoading && !demandsError && openDemands.length > 0 && (
                                <S.PanelFilter>
                                    <S.PanelSearchWrap>
                                        <Input
                                            allowClear
                                            value={demandQuery}
                                            onChange={(e) => setDemandQuery(e.target.value)}
                                            placeholder="Filter by role, company, city"
                                            prefix={<SearchOutlined style={{ color: "#9AA3BA" }} />}
                                        />
                                    </S.PanelSearchWrap>
                                    <S.MiniChipRow>
                                            <S.MiniChip
                                                type="button"
                                                $active={!demandLocation && !demandExp}
                                                onClick={() => {
                                                    setDemandLocation(null);
                                                    setDemandExp(null);
                                                }}
                                            >
                                                All
                                            </S.MiniChip>
                                            {DEMAND_EXP_BUCKETS.map((bucket) => (
                                                <S.MiniChip
                                                    key={bucket.key}
                                                    type="button"
                                                    $active={demandExp === bucket.key}
                                                    onClick={() =>
                                                        setDemandExp((prev) => (prev === bucket.key ? null : bucket.key))
                                                    }
                                                >
                                                    {bucket.label}
                                                </S.MiniChip>
                                            ))}
                                            {demandLocations.slice(0, 6).map((city) => (
                                                <S.MiniChip
                                                    key={city}
                                                    type="button"
                                                    $active={demandLocation === city}
                                                    onClick={() =>
                                                        setDemandLocation((prev) => (prev === city ? null : city))
                                                    }
                                                >
                                                    {city}
                                                </S.MiniChip>
                                            ))}
                                        </S.MiniChipRow>
                                </S.PanelFilter>
                            )}

                            {demandsLoading && (
                                <div style={{ padding: "12px 0", textAlign: "center" }}>
                                    <Spin size="small" />
                                </div>
                            )}

                            {!demandsLoading && demandsError && (
                                <S.PanelEmpty>Couldn't load open demands right now.</S.PanelEmpty>
                            )}

                            {!demandsLoading && !demandsError && openDemands.length === 0 && (
                                <S.PanelEmpty>No open demands right now.</S.PanelEmpty>
                            )}

                            {!demandsLoading && !demandsError && openDemands.length > 0 && (
                            <S.PanelList>
                            {filteredOpenDemands.length === 0 ? (
                                <S.PanelEmpty>No demands match these filters.</S.PanelEmpty>
                            ) : (
                                filteredOpenDemands.map((demand) => {
                                    const hasJd = typeof demand.job_desc === "string" && demand.job_desc.trim() !== "";
                                    const summary = describeDemand(demand);
                                    return (
                                        <S.PanelItem
                                            key={demand.id}
                                            type="button"
                                            onClick={() => searchOpenDemand(demand)}
                                            disabled={isBusy || !hasJd}
                                            title={hasJd ? "Find candidates using this demand's JD" : "No JD attached to this demand"}
                                        >
                                            <S.PanelItemTitle title={demand.role_title || "Untitled role"}>
                                                {demand.role_title || "Untitled role"}
                                            </S.PanelItemTitle>
                                            <S.PanelItemMeta>
                                                {[demand.company_name, summary].filter(Boolean).join(" · ")}
                                                {!hasJd && " · no JD"}
                                            </S.PanelItemMeta>
                                        </S.PanelItem>
                                    );
                                })
                            )}
                                <S.PanelListEnd>
                                    {filteredOpenDemands.length === 0 ? "No matching demands" : "End of open demands"}
                                </S.PanelListEnd>
                            </S.PanelList>
                            )}

                            {!demandsLoading && !demandsError && (
                                <S.PanelFooter>
                                    {openDemands.length === 0
                                        ? "No open demands"
                                        : filteredOpenDemands.length === openDemands.length
                                        ? `${openDemands.length} open demand${openDemands.length === 1 ? "" : "s"}`
                                        : `Showing ${filteredOpenDemands.length} of ${openDemands.length}`}
                                </S.PanelFooter>
                            )}
                                    </S.Popover>
                                )}
                            </S.PopoverAnchor>
                        </S.TopActions>
                    </S.TopBar>

                    {/* ════ SEARCH ════
                       Centered before the first search; after it the composer
                       stays at the top and the results follow. */}
                    <S.SearchStage $centered={!hasSearched}>
                        {!hasSearched && (
                            <S.Hero>
                                <S.HeroTitle>Find the right candidates</S.HeroTitle>
                                <S.HeroSubtitle>Search using keywords or a job description.</S.HeroSubtitle>
                            </S.Hero>
                        )}

                        <S.Composer
                            $dragging={dragActive}
                            onDragOver={(e) => {
                                e.preventDefault();
                                if (!dragActive) setDragActive(true);
                            }}
                            onDragLeave={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false);
                            }}
                            onDrop={onComposerDrop}
                        >
                            {inputMode === "jd" && (
                                <S.ComposerAttachments>
                                    {!jdFile && !jdUploading && !jdImageFile && !jdImageUploading && (
                                        <S.AttachmentChip title="Searching with a job description">
                                            <FileTextOutlined />
                                            <span className="name">Job description</span>
                                            <S.ChipRemove
                                                type="button"
                                                onClick={exitJdTextMode}
                                                aria-label="Back to keyword search"
                                                title="Back to keyword search"
                                            >
                                                <CloseOutlined />
                                            </S.ChipRemove>
                                        </S.AttachmentChip>
                                    )}
                                    {(jdFile || jdUploading) && (jdUploading ? (
                                        <S.AttachmentChip>
                                            <LoadingOutlined />
                                            <span className="name">Uploading JD…</span>
                                        </S.AttachmentChip>
                                    ) : (
                                        <S.AttachmentChip title={jdFile.name}>
                                            <FilePdfOutlined />
                                            <span className="name">{jdFile.name}</span>
                                            <S.ChipRemove
                                                type="button"
                                                onClick={removeJdFileChip}
                                                aria-label="Remove JD file"
                                                title="Remove JD file"
                                            >
                                                <CloseOutlined />
                                            </S.ChipRemove>
                                        </S.AttachmentChip>
                                    ))}
                                    {jdImageUploading && (
                                        <S.AttachmentChip>
                                            <LoadingOutlined />
                                            <span className="name">Uploading image…</span>
                                        </S.AttachmentChip>
                                    )}
                                    {jdImageFile && !jdImageUploading && (
                                        <S.AttachmentChip title={jdImageFile.name}>
                                            {jdImagePreview ? (
                                                <S.AttachmentThumb src={jdImagePreview} alt="" />
                                            ) : (
                                                <PictureOutlined />
                                            )}
                                            <span className="name">{jdImageFile.name}</span>
                                            <S.ChipRemove
                                                type="button"
                                                onClick={removeJdImageChip}
                                                aria-label="Remove JD image"
                                                title="Remove JD image"
                                            >
                                                <CloseOutlined />
                                            </S.ChipRemove>
                                        </S.AttachmentChip>
                                    )}
                                </S.ComposerAttachments>
                            )}

                            <S.ComposerRow>
                                {/* + menu */}
                                <S.PopoverAnchor ref={plusRef}>
                                    <S.PlusButton
                                        type="button"
                                        $active={openPanel === "plus"}
                                        onClick={() => togglePanel("plus")}
                                        aria-expanded={openPanel === "plus"}
                                        aria-haspopup="menu"
                                        aria-label="Add a job description"
                                        title="Add a job description"
                                        disabled={jdUploading || jdImageUploading}
                                    >
                                        <PlusOutlined />
                                    </S.PlusButton>
                                    {openPanel === "plus" && (
                                        <S.PlusMenu role="menu">
                                            <S.PlusMenuItem type="button" role="menuitem" onClick={() => choosePlusAction("jd-file")}>
                                                <S.PlusMenuIcon><FilePdfOutlined /></S.PlusMenuIcon>
                                                <span>
                                                    Add Job Description
                                                    <small>Upload a JD PDF up to 2MB</small>
                                                </span>
                                            </S.PlusMenuItem>
                                            <S.PlusMenuItem type="button" role="menuitem" onClick={() => choosePlusAction("image")}>
                                                <S.PlusMenuIcon><PictureOutlined /></S.PlusMenuIcon>
                                                <span>
                                                    Add Image
                                                    <small>JD screenshot · PNG, JPG or WEBP up to 2MB</small>
                                                </span>
                                            </S.PlusMenuItem>
                                            <S.PlusMenuItem type="button" role="menuitem" onClick={() => choosePlusAction("jd-text")}>
                                                <S.PlusMenuIcon><FormOutlined /></S.PlusMenuIcon>
                                                <span>
                                                    Add Text
                                                    <small>Paste or type the JD text</small>
                                                </span>
                                            </S.PlusMenuItem>
                                        </S.PlusMenu>
                                    )}
                                </S.PopoverAnchor>


                                <S.ComposerInput>
                                    <Input.TextArea
                                        ref={keywordInputRef}
                                        value={inputMode === "jd" ? jdText : keywordQuery}
                                        onChange={(e) =>
                                            inputMode === "jd" ? setJdText(e.target.value) : setKeywordQuery(e.target.value)
                                        }
                                        onPaste={onComposerPaste}
                                        onPressEnter={(e) => {
                                            // Keywords: Enter searches (as before). A JD is
                                            // multi-line: Enter adds a line, Ctrl/Cmd+Enter searches.
                                            if (inputMode === "jd") {
                                                if (e.ctrlKey || e.metaKey) {
                                                    e.preventDefault();
                                                    handleSubmit();
                                                }
                                                return;
                                            }
                                            e.preventDefault();
                                            handleKeywordSearch();
                                        }}
                                        placeholder={
                                            inputMode === "jd"
                                                ? jdFile || jdImageFile
                                                    ? "JD attached — press the arrow to search, or paste text instead"
                                                    : "Paste the job description text here…"
                                                : "Search candidates by role, skills, location, experience, budget..."
                                        }
                                        maxLength={inputMode === "jd" ? undefined : KEYWORD_QUERY_MAX_LENGTH}
                                        autoSize={inputMode === "jd" ? { minRows: 1, maxRows: 8 } : { minRows: 1, maxRows: 3 }}
                                        aria-label={inputMode === "jd" ? "Job description text" : "Search keywords"}
                                    />
                                </S.ComposerInput>

                                <S.ComposerActions>
                                    <S.IconButton
                                        type="button"
                                        onClick={handleClear}
                                        disabled={jdUploading}
                                        title="Clear search"
                                        aria-label="Clear search"
                                    >
                                        <ReloadOutlined />
                                    </S.IconButton>
                                    <S.SubmitButton
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={submitDisabled}
                                        title={inputMode === "jd" ? "Search with JD" : "Search candidates"}
                                        aria-label={inputMode === "jd" ? "Search with JD" : "Search candidates"}
                                    >
                                        {isBusy ? <LoadingOutlined /> : <ArrowRightOutlined />}
                                    </S.SubmitButton>
                                </S.ComposerActions>
                            </S.ComposerRow>

                            {/* Hidden PDF picker for + → Upload JD / Document */}
                            <input
                                ref={jdFileInputRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={onJdFileChosen}
                                style={{ display: "none" }}
                                tabIndex={-1}
                            />

                            {/* Hidden image picker for + → Add Image */}
                            <input
                                ref={jdImageInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={onJdImageChosen}
                                style={{ display: "none" }}
                                tabIndex={-1}
                            />
                        </S.Composer>

                        {inputMode === "jd" && jdText.trim().length > 0 && (
                            <S.JdCharCount $error={jdText.trim().length < 250} style={{ paddingLeft: 18 }}>
                                {jdText.trim().length}/250 characters minimum · Ctrl+Enter to search
                            </S.JdCharCount>
                        )}

                        {activeChips.length > 0 && (
                            <S.FilterChipRow>
                                <S.FilterChipLabel>Searching for</S.FilterChipLabel>
                                {activeChips.map((chip) => (
                                    <S.RemovableChip
                                        key={chip.key}
                                        type="button"
                                        title={`Remove ${chip.label}`}
                                        onClick={() => removeActiveChip(chip)}
                                        disabled={isBusy}
                                    >
                                        {chip.label}
                                        <CloseOutlined />
                                    </S.RemovableChip>
                                ))}
                            </S.FilterChipRow>
                        )}

                        {refineChips.length > 0 && (
                            <S.FilterChipRow>
                                <S.FilterChipLabel>Refined by</S.FilterChipLabel>
                                {refineChips.map((chip) => (
                                    <S.RemovableChip
                                        key={chip.key}
                                        type="button"
                                        title={`Remove ${chip.label}`}
                                        onClick={() => removeRefineChip(chip)}
                                        disabled={isBusy}
                                    >
                                        {chip.label}
                                        <CloseOutlined />
                                    </S.RemovableChip>
                                ))}
                            </S.FilterChipRow>
                        )}

                        {searchMode === "jd" && activeDemandLabel && (
                            <S.FilterChipRow>
                                <S.FilterChipLabel>Searching with the JD of</S.FilterChipLabel>
                                <S.SkillPill>{activeDemandLabel}</S.SkillPill>
                            </S.FilterChipRow>
                        )}
                    </S.SearchStage>

                    {/* ════ RESULTS ════ */}
                    {hasSearched && (
                    <S.ResultsBlock>
                        <S.ResultsHeader ref={resultsAnchorRef}>
                            <div>
                                <S.Eyebrow>SEARCH RESULTS</S.Eyebrow>
                                <S.ResultsTitle>
                                    {loading ? "Finding candidates…" : `${total} ${total === 1 ? "candidate" : "candidates"} found`}
                                </S.ResultsTitle>
                            </div>
                        </S.ResultsHeader>

                    {loading && (
                        <S.StatusCard>
                            <Spin tip="Searching candidates..." />
                        </S.StatusCard>
                    )}

                    {showZeroResults && (
                        <S.StatusCard>
                            <img
                                src={require("../../images/Frame.png")}
                                alt="No Data"
                                style={{ width: "22%", maxWidth: "180px", height: "auto" }}
                            />
                            <S.EmptyTitle>No candidates found</S.EmptyTitle>
                            <S.EmptyText>
                                {refineActive
                                    ? "No candidates match the refine filters. Remove a filter to see more."
                                    : "Try different keywords or a different JD."}
                            </S.EmptyText>
                        </S.StatusCard>
                    )}

                    {showTable && (
                        <S.CandidateGrid>
                            {itemsToDisplay.map((candidate, index) => {
                                const skillsInfo = getSkillsSummary(candidate);
                                const name = getCandidateName(candidate);
                                const roleValue = (candidate?.current_role || "").trim();
                                const qualificationValue = (candidate?.highest_qualification || "").trim();
                                const locationValue = (candidate?.candidate_location || "").trim();
                                const rolePositionTitle = getCurrentPosition(candidate);

                                // Parsing Based Matched Score: the single backend match_score
                                // (same field in JD and keyword mode), with its point breakdown
                                // as the hover text.
                                const matchScoreLabel = formatMatchPercentage(candidate?.match_score);
                                const matchScoreTitle = describeScoreBreakdown(
                                    candidate?.match_score,
                                    candidate?.score_breakdown
                                );
                                // platform_created_at = when this candidate FIRST entered the
                                // platform (earliest record for their email).
                                const createdDateLabel = formatCreatedDate(candidate?.platform_created_at);
                                // Latest Round: backend latest_round_cleared (same value used
                                // for sorting), existing client-side logic only as a fallback.
                                const roundCleared = getLatestRoundCleared(candidate);
                                // Test Score: backend tech_score (hidden below 65, as before).
                                const techScoreValue = getTechScoreValue(candidate);
                                // Fitment Analysis: the AI score generated for this candidate
                                // against the current search.
                                const aiScoreLabel = getGeneratedAiScoreLabel(candidate);

                                // Generate AI Score on the card: shown only while a search is
                                // active and no score exists yet; same handler as the popup.
                                const cardAiKey = aiScoreKey(candidate?.id, activeSearchSource);
                                const aiGenerating = Boolean(cardAiKey) && aiState.loading && aiState.key === cardAiKey;
                                const canGenerateAi = Boolean(activeSearchSource) && !aiScoreLabel;

                                const contactShown = isContactRevealed(candidate?.id);
                                const hasContact = Boolean(candidate?.primary_no || candidate?.email);

                                return (
                                    <S.CandidateCard key={candidate?.id || index}>
                                        {/* 1 · Header: avatar, name, View Contact / View Resume */}
                                        <S.CardHeader>
                                            <CandidateAvatar name={name} photoUrl={candidate?.profile_image} size={64} />
                                            <S.CardIdentity>
                                                <S.CardName
                                                    title={name}
                                                    onClick={() => openCandidateDetails(candidate)}
                                                >
                                                    {name}
                                                </S.CardName>
                                                {/* platform_created_at — subtle, small, directly under the name. */}
                                                {createdDateLabel && (
                                                    <S.CreatedStamp>Created: {createdDateLabel}</S.CreatedStamp>
                                                )}
                                                <S.CardButtons>
                                                    <S.PillButton
                                                        type="button"
                                                        $solid
                                                        onClick={() => toggleContact(candidate?.id)}
                                                        disabled={!hasContact}
                                                        title={hasContact ? "Show or hide phone and email" : "No contact details available"}
                                                    >
                                                        <PhoneOutlined /> {contactShown ? "Hide Contact" : "View Contact"}
                                                    </S.PillButton>
                                                    {candidate?.cv_url ? (
                                                        <S.PillButton
                                                            as="a"
                                                            href={candidate.cv_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <FilePdfOutlined /> View Resume
                                                        </S.PillButton>
                                                    ) : (
                                                        <S.PillButton type="button" disabled title="No resume available">
                                                            <FilePdfOutlined /> View Resume
                                                        </S.PillButton>
                                                    )}
                                                </S.CardButtons>
                                                {contactShown && hasContact && (
                                                    <S.ContactReveal>
                                                        {candidate?.primary_no && (
                                                            <S.ContactItem title={candidate.primary_no}>
                                                                <PhoneOutlined /> {candidate.primary_no}
                                                            </S.ContactItem>
                                                        )}
                                                        {candidate?.email && (
                                                            <S.ContactItem title={candidate.email}>
                                                                <MailOutlined /> {candidate.email}
                                                            </S.ContactItem>
                                                        )}
                                                    </S.ContactReveal>
                                                )}
                                            </S.CardIdentity>
                                        </S.CardHeader>

                                        {/* 2 · Info row: role · qualification · experience · location */}
                                        <S.InfoRow>
                                            <S.InfoItem title={rolePositionTitle || undefined}>
                                                <IdcardOutlined />
                                                <span>{roleValue || "-"}</span>
                                            </S.InfoItem>
                                            <S.InfoItem title={qualificationValue || undefined}>
                                                <ReadOutlined />
                                                <span>{qualificationValue || "-"}</span>
                                            </S.InfoItem>
                                            <S.InfoItem>
                                                <ClockCircleOutlined />
                                                <span>{formatExperience(candidate?.total_work_exp)}</span>
                                            </S.InfoItem>
                                            <S.InfoItem title={locationValue || undefined}>
                                                <EnvironmentOutlined />
                                                <span>{locationValue || "-"}</span>
                                            </S.InfoItem>
                                        </S.InfoRow>

                                        {/* 3 · Skills — the searched skills that matched (profile OR
                                           resume) first, then the candidate's other profile skills. */}
                                        <S.SkillsSection>
                                            <S.SkillsLabel>Skills</S.SkillsLabel>
                                            {skillsInfo.visible.length > 0 ? (
                                                <S.SkillPillRow>
                                                    {skillsInfo.visible.map((skill, i) => (
                                                        <S.SkillPill key={`${skill}-${i}`} title={skill}>{skill}</S.SkillPill>
                                                    ))}
                                                    {skillsInfo.rest.length > 0 && (
                                                        <Tooltip title={skillsInfo.rest.join(", ")}>
                                                            <S.SkillMore>+{skillsInfo.rest.length}</S.SkillMore>
                                                        </Tooltip>
                                                    )}
                                                </S.SkillPillRow>
                                            ) : (
                                                <S.MutedDash>-</S.MutedDash>
                                            )}
                                        </S.SkillsSection>

                                        {/* 4 · Score panel — four equal cells, always all rendered. */}
                                        <S.ScorePanel>
                                            <Tooltip title={matchScoreTitle}>
                                                <S.ScoreCell>
                                                    <S.ScoreCellHead>
                                                        <BlockOutlined />
                                                        <span>Parsing Based Matched Score</span>
                                                    </S.ScoreCellHead>
                                                    <S.ScoreCellValue $tone={matchScoreLabel ? "accent" : "muted"}>
                                                        {matchScoreLabel || "—"}
                                                    </S.ScoreCellValue>
                                                </S.ScoreCell>
                                            </Tooltip>

                                            <Tooltip title={roundCleared ? `Cleared interview round ${roundCleared}` : "No interview round cleared yet"}>
                                                <S.ScoreCell>
                                                    <S.ScoreCellHead>
                                                        <TrophyOutlined />
                                                        <span>Latest Round</span>
                                                    </S.ScoreCellHead>
                                                    {roundCleared ? (
                                                        <S.RoundBadge>R{roundCleared}</S.RoundBadge>
                                                    ) : (
                                                        <S.ScoreCellValue $tone="muted">—</S.ScoreCellValue>
                                                    )}
                                                </S.ScoreCell>
                                            </Tooltip>

                                            <Tooltip title="Technical assessment score">
                                                <S.ScoreCell>
                                                    <S.ScoreCellHead>
                                                        <FileTextOutlined />
                                                        <span>Test Score</span>
                                                    </S.ScoreCellHead>
                                                    <S.ScoreCellValue $tone={techScoreValue !== null ? "dark" : "muted"}>
                                                        {techScoreValue !== null ? `${techScoreValue}/100` : "—"}
                                                    </S.ScoreCellValue>
                                                </S.ScoreCell>
                                            </Tooltip>

                                            <Tooltip
                                                title={
                                                    aiScoreLabel
                                                        ? `AI fitment against: ${activeSearchSource?.label || "this search"}`
                                                        : "Click Generate AI Score to see this"
                                                }
                                            >
                                                <S.ScoreCell>
                                                    <S.ScoreCellHead>
                                                        <StarOutlined />
                                                        <span>Fitment Analysis</span>
                                                    </S.ScoreCellHead>
                                                    <S.ScoreCellValue $tone={aiScoreLabel ? "accent" : "muted"}>
                                                        {aiScoreLabel || "—"}
                                                    </S.ScoreCellValue>
                                                </S.ScoreCell>
                                            </Tooltip>
                                        </S.ScorePanel>

                                        {/* 5 · Footer: Expected CTC · Generate AI Score · View More */}
                                        <S.CardFooter>
                                            <S.CtcBlock>
                                                <DollarCircleOutlined />
                                                <span className="label">Expected CTC:</span>
                                                <b>{formatCtc(candidate?.expected_ctc)}</b>
                                            </S.CtcBlock>
                                            <S.FooterActions>
                                                {canGenerateAi && (
                                                    <S.AiScoreButton
                                                        type="button"
                                                        onClick={() =>
                                                            generateAiScoreForCandidate(candidate, { notifyErrors: true })
                                                        }
                                                        disabled={aiState.loading}
                                                        title={`Score this candidate against: ${activeSearchSource?.label || "this search"}`}
                                                    >
                                                        {aiGenerating ? <LoadingOutlined /> : <ThunderboltOutlined />}
                                                        {aiGenerating ? "Generating…" : "Generate AI Score"}
                                                    </S.AiScoreButton>
                                                )}
                                                <S.ViewMoreButton
                                                    type="button"
                                                    onClick={() => openCandidateDetails(candidate)}
                                                >
                                                    View More <ArrowRightOutlined />
                                                </S.ViewMoreButton>
                                            </S.FooterActions>
                                        </S.CardFooter>
                                    </S.CandidateCard>
                                );
                            })}
                        </S.CandidateGrid>
                    )}

                    {/* ── Pagination ── */}
                    {showTable && (
                        <S.PaginationBar>
                            <S.PaginationMeta>
                                <span>Show</span>
                                <S.PaginationSelectWrapper>
                                    <Select
                                        value={pageSize}
                                        size="small"
                                        popupMatchSelectWidth={false}
                                        onChange={handlePageSizeChange}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <Select.Option key={size} value={size}>
                                                {size}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </S.PaginationSelectWrapper>
                                <span>per page</span>
                                <span style={{ color: "#9CA3AF" }}>|</span>
                                <span>
                                    {pageStart + 1}–{Math.min(pageStart + pageSize, total)} of {total} results
                                </span>
                            </S.PaginationMeta>
                            <S.PaginationControls>
                                <S.PageButton
                                    type="button"
                                    $nav
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                >
                                    ← Prev
                                </S.PageButton>
                                {buildPageNumbers(currentPage, totalPages).map((page, idx) =>
                                    page === "..." ? (
                                        <S.PageEllipsis key={`ellipsis-${idx}`}>…</S.PageEllipsis>
                                    ) : (
                                        <S.PageButton
                                            key={page}
                                            type="button"
                                            $active={page === currentPage}
                                            onClick={() => handlePageChange(page)}
                                        >
                                            {page}
                                        </S.PageButton>
                                    )
                                )}
                                <S.PageButton
                                    type="button"
                                    $nav
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                >
                                    Next →
                                </S.PageButton>
                            </S.PaginationControls>
                        </S.PaginationBar>
                    )}
                    </S.ResultsBlock>
                    )}
                </S.ContentWrap>

                {/* ════ DETAIL MODAL ════ */}
                {selectedCandidate && (() => {
                    // While details are loading (or if they failed), fall back to
                    // the lightweight search-result row for the header so the
                    // modal never looks empty.
                    const detailSource = candidateDetail?.candidate || selectedCandidate;
                    const headerName = getCandidateName(detailSource);

                    return (
                        <Modal
                            open={!!selectedCandidate}
                            onCancel={closeCandidateDetails}
                            footer={null}
                            width={975}
                            centered
                            wrapClassName="coffeee-plus-detail-modal-wrap"
                            closeIcon={<S.ModalCloseIcon><CloseOutlined /></S.ModalCloseIcon>}
                            styles={{ body: { padding: "22px 24px 24px" } }}
                        >
                            <S.ModalProfileRow>
                                <CandidateAvatar
                                    name={headerName}
                                    size={84}
                                    photoUrl={detailSource?.profile_image || selectedCandidate?.profile_image}
                                />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <S.ModalName>{headerName}</S.ModalName>
                                    <S.ModalRoleCompany>
                                        {detailSource?.current_role || "-"} · {detailSource?.current_company || "-"}
                                    </S.ModalRoleCompany>
                                    <S.ModalContactRow>
                                        {!showContact ? (
                                            <S.PrimaryAction
                                                type="button"
                                                onClick={() => setShowContact(true)}
                                            >
                                                View contact
                                            </S.PrimaryAction>
                                        ) : (
                                            <>
                                                {detailSource?.email && (
                                                    <S.ModalContactLink href={`mailto:${detailSource.email}`}>
                                                        <MailOutlined /> {detailSource.email}
                                                    </S.ModalContactLink>
                                                )}

                                                {detailSource?.primary_no && (
                                                    <S.ModalContactLink href={`tel:${detailSource.primary_no}`}>
                                                        <PhoneOutlined /> {detailSource.primary_no}
                                                    </S.ModalContactLink>
                                                )}
                                            </>
                                        )}
                                        {detailSource?.cv_url && (
                                            <S.ModalContactLink href={detailSource.cv_url} target="_blank" rel="noopener noreferrer">
                                                <FilePdfOutlined /> View resume
                                            </S.ModalContactLink>
                                        )}
                                    </S.ModalContactRow>
                                </div>
                            </S.ModalProfileRow>

                            {detailLoading && (
                                <S.ModalSection style={{ textAlign: "center" }}>
                                    <Spin tip="Loading candidate details..." />
                                </S.ModalSection>
                            )}

                            {!detailLoading && detailError && (
                                <S.ModalSection>
                                    <S.FieldError>{detailError}</S.FieldError>
                                </S.ModalSection>
                            )}

                            {!detailLoading && !detailError && candidateDetail && (() => {
                                // Pure presence check — 0 is a real value and is shown.
                                const hasValue = (v) =>
                                    v !== null && v !== undefined && !(typeof v === "string" && v.trim() === "");

                                // Only fields / sections that have data are rendered; nothing
                                // shows a placeholder like "—" or "Not attempted".
                                const ov = candidateDetail.overview || {};
                                const overviewCells = [
                                    ["Total Experience", ov.total_experience, formatExperience],
                                    ["Relevant Experience", ov.relevant_experience, formatExperience],
                                    ["Last Known Current CTC", ov.last_known_current_ctc, formatCtc],
                                    ["Last Known Expected CTC", ov.last_known_expected_ctc, formatCtc],
                                    ["Last Known Current Loc", ov.last_known_location, null],
                                ].filter(([, value]) => hasValue(value));
                                const primarySkills = Array.isArray(candidateDetail.skills?.primary)
                                    ? candidateDetail.skills.primary.filter(Boolean)
                                    : [];
                                const assessment = candidateDetail.assessment || {};

                                // The search row carries the backend's latest_round_cleared (the
                                // value used for sorting); the detail payload only has the raw
                                // status fields, so it is the fallback.
                                const latestRound = getLatestRoundCleared(
                                    Object.prototype.hasOwnProperty.call(selectedCandidate || {}, "latest_round_cleared")
                                        ? selectedCandidate
                                        : candidateDetail.candidate || {}
                                );

                                // Same % and hover text as the card's "Parsing Based Matched Score".
                                const row = selectedCandidate || {};
                                const matchLabel = formatMatchPercentage(row.match_score);
                                const matchTitle = describeScoreBreakdown(row.match_score, row.score_breakdown);

                                const aiKey = aiScoreKey(row.id, activeSearchSource);
                                const aiCurrent = aiState.key === aiKey ? aiState : { loading: false, error: null, result: null };
                                const aiResult = aiCurrent.result;
                                const aiSkills = Array.isArray(aiResult?.skills) ? aiResult.skills : [];
                                const aiRisks = Array.isArray(aiResult?.risk_flags) ? aiResult.risk_flags.filter(hasValue) : [];
                                const aiCerts = Array.isArray(aiResult?.certifications) ? aiResult.certifications : [];

                                return (
                                    <>
                                        {/* 1. Candidate Overview — only the fields that have a value */}
                                        {overviewCells.length > 0 && (
                                            <S.ModalSection>
                                                <S.Eyebrow>CANDIDATE OVERVIEW</S.Eyebrow>
                                                <S.DetailGrid>
                                                    {overviewCells.map(([label, value, format]) => (
                                                        <S.DetailCell key={label}>
                                                            <span>{label}</span>
                                                            <b>{format ? format(value) : value}</b>
                                                        </S.DetailCell>
                                                    ))}
                                                </S.DetailGrid>
                                            </S.ModalSection>
                                        )}

                                        {/* 2. Skills — primary only, with the card's match %; hidden if neither exists */}
                                        {(primarySkills.length > 0 || matchLabel) && (
                                            <S.ModalSection>
                                                <S.SectionHeadRow>
                                                    <S.Eyebrow>SKILLS</S.Eyebrow>
                                                    {matchLabel && (
                                                        <Tooltip title={matchTitle}>
                                                            <S.InlineScoreBadge>{matchLabel} match</S.InlineScoreBadge>
                                                        </Tooltip>
                                                    )}
                                                </S.SectionHeadRow>
                                                {primarySkills.length > 0 && (
                                                    <S.SkillPillRow style={{ marginTop: 12 }}>
                                                        {primarySkills.map((skill) => (
                                                            <S.SkillPill key={skill} title={skill}>{skill}</S.SkillPill>
                                                        ))}
                                                    </S.SkillPillRow>
                                                )}
                                            </S.ModalSection>
                                        )}

                                        {/* 3. Assessment — only when attempted */}
                                        {assessment.attempted && (
                                            <S.ModalSection>
                                                <S.SectionHeadRow>
                                                    <S.Eyebrow>ASSESSMENT</S.Eyebrow>
                                                    {assessment.assessment_id && (
                                                        <S.PrimaryAction
                                                            type="button"
                                                            onClick={() => downloadAssessmentReport(assessment.assessment_id, headerName)}
                                                            disabled={downloadingReport}
                                                        >
                                                            {downloadingReport ? <LoadingOutlined /> : <DownloadOutlined />}
                                                            {downloadingReport ? "Downloading…" : "Download assessment report"}
                                                        </S.PrimaryAction>
                                                    )}
                                                </S.SectionHeadRow>
                                                <S.DetailGrid>
                                                    <S.DetailCell><span>Status</span><b>Attempted</b></S.DetailCell>
                                                    {isTechScoreVisible(assessment.tech_score) && (
                                                        <S.DetailCell>
                                                            <span>Assessment Score</span>
                                                            <b>{`${Math.round(Number(assessment.tech_score))}%`}</b>
                                                        </S.DetailCell>
                                                    )}
                                                </S.DetailGrid>
                                            </S.ModalSection>
                                        )}

                                        {/* 4. AI Assessment — only after a search (its Generate button
                                           creates the data, so the section stays while a search is active) */}
                                        {activeSearchSource && (
                                        <S.ModalSection>
                                            <S.Eyebrow>AI ASSESSMENT</S.Eyebrow>
                                                <>
                                                    <S.SourceActionRow>
                                                        <S.AiSourceLine>
                                                            Scored against: <b title={activeSearchSource.label}>{activeSearchSource.label}</b>
                                                        </S.AiSourceLine>
                                                        {!aiResult && (
                                                            <S.PrimaryAction
                                                                type="button"
                                                                onClick={() => generateAiScoreForCandidate(selectedCandidate)}
                                                                disabled={aiCurrent.loading}
                                                            >
                                                                {aiCurrent.loading ? <LoadingOutlined /> : <ThunderboltOutlined />}
                                                                {aiCurrent.loading ? "Generating… up to a minute" : "Generate AI score"}
                                                            </S.PrimaryAction>
                                                        )}
                                                    </S.SourceActionRow>

                                                    {aiCurrent.error && <S.FieldError style={{ marginTop: 8 }}>{aiCurrent.error}</S.FieldError>}

                                                    {aiResult && (
                                                        <>
                                                            <S.DetailGrid>
                                                                <S.DetailCell><span>AI Score</span><b>{aiResult.overall_score} / 100</b></S.DetailCell>
                                                                {hasValue(aiResult.recommendation) && (
                                                                    <S.DetailCell><span>Verdict</span><b>{aiResult.recommendation}</b></S.DetailCell>
                                                                )}
                                                                {hasValue(aiResult.generated_at) && (
                                                                    <S.DetailCell><span>Generated</span><b>{new Date(aiResult.generated_at).toLocaleString()}</b></S.DetailCell>
                                                                )}
                                                            </S.DetailGrid>

                                                            {aiSkills.length > 0 && (
                                                                <div style={{ marginTop: 16 }}>
                                                                    <S.EmptyText style={{ fontWeight: 700, marginBottom: 8 }}>Skill Assessment</S.EmptyText>
                                                                    {aiSkills.map((sk, idx) => (
                                                                        <S.RecordCard key={idx}>
                                                                            <S.RecordRow>
                                                                                {hasValue(sk.skill) && <b>{sk.skill}</b>}
                                                                                {hasValue(sk.status) && <S.SkillPill>{sk.status}</S.SkillPill>}
                                                                            </S.RecordRow>
                                                                            {hasValue(sk.applied_experience) && <S.EmptyText>Experience: {sk.applied_experience}</S.EmptyText>}
                                                                            {hasValue(sk.evidence) && <S.EmptyText>Evidence: {sk.evidence}</S.EmptyText>}
                                                                        </S.RecordCard>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {aiRisks.length > 0 && (
                                                                <div style={{ marginTop: 16 }}>
                                                                    <S.EmptyText style={{ fontWeight: 700, marginBottom: 8 }}>Risk Flags</S.EmptyText>
                                                                    {aiRisks.map((flag, idx) => (
                                                                        <S.EmptyText key={idx}>• {flag}</S.EmptyText>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {aiCerts.length > 0 && (
                                                            <div style={{ marginTop: 16 }}>
                                                                <S.EmptyText style={{ fontWeight: 700, marginBottom: 8 }}>Certifications</S.EmptyText>
                                                                    <S.CertList>
                                                                        {aiCerts.map((cert, idx) => (
                                                                            <S.CertItem key={`${cert.name}-${idx}`}>
                                                                                <SafetyCertificateOutlined />
                                                                                <span>
                                                                                    <b>{cert.name}</b>
                                                                                    {(cert.issuer || cert.year) && (
                                                                                        <small>{[cert.issuer, cert.year].filter(Boolean).join(" · ")}</small>
                                                                                    )}
                                                                                </span>
                                                                            </S.CertItem>
                                                                        ))}
                                                                    </S.CertList>
                                                            </div>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                        </S.ModalSection>
                                        )}

                                        {/* 5. Interview History — only when a round has been completed */}
                                        {latestRound && (
                                            <S.ModalSection>
                                                <S.Eyebrow>INTERVIEW HISTORY</S.Eyebrow>
                                                <S.DetailGrid>
                                                    <S.DetailCell><span>Latest Round Completed</span><b>R{latestRound}</b></S.DetailCell>
                                                </S.DetailGrid>
                                            </S.ModalSection>
                                        )}
                                    </>
                                );
                            })()}
                        </Modal>
                    );
                })()}
            </S.PageWrapper>
        </>
    );
};

export default CoffeeePlusSearch;